import React, { useState, useMemo, useEffect, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Plus,
  Minus,
  X,
  Search,
  Printer as PrinterIcon,
  Utensils,
  ShoppingBag,
  Truck,
  User,
  Clock,
  Flame,
  Grid3x3,
  ChevronRight,
  ChevronLeft,
  Loader2,
  Save,
  Gift,
  History,
  Menu,
  Pause,
  RotateCcw,
  Percent,
  Send,
  MessageSquare,
  DollarSign,
  Package,
  Undo2,
  ArrowLeft,
  CreditCard,
  Zap,
  Layers,
  Users,
  Banknote,
  Trash2,
  Receipt,
  Scissors,
  Merge,
  ArrowRightLeft,
  UserCog,
  ClipboardList,
  ListOrdered,
  Wallet,
  Lock,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { aed, money2, qty2, roundOffToUnit } from "@/lib/format";
import { useCurrency } from "@/hooks/useCurrency";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { cancelPreparedBillPrint, prepareBillPrintWindow, printBillReceipt } from "@/lib/billPrint";
import { dispatchKotJobsLan, runKotReprint } from "@/lib/kotPrint";
import ClockWeatherWidget from "@/components/dashboard/ClockWeatherWidget";
import DeliveryPanel from "@/components/pos/DeliveryPanel";
import DayOpenGate from "@/components/pos/DayOpenGate";
import PosKeypadPopup from "@/components/pos/PosKeypadPopup";
import PosCreditPopup from "@/components/pos/PosCreditPopup";
import PosBillReprintPopup, { todayYmd } from "@/components/pos/PosBillReprintPopup";
import PosCashReceiptPopup from "@/components/pos/PosCashReceiptPopup";
import PosSizePickerPopup from "@/components/pos/PosSizePickerPopup";
import { itemHasSizes } from "@/lib/menuItemSizes";
import { displayItemName, itemNameSecondary } from "@/lib/menuLocale";
import PosEmptyIllustration from "@/components/pos/PosEmptyIllustration";
import PosProductButton from "@/components/pos/PosProductButton";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { runPosBootstrap } from "@/lib/posBootstrap";
import {
  annotateBillLinesForDisplay,
  billLineActionId,
  findStandaloneBillLine,
} from "@/lib/billLines";
import { useLocale } from "@/contexts/LocaleProvider";
import { useCounterShift } from "@/hooks/useCounterShift";
import { useOfflinePosSync } from "@/hooks/useOfflinePosSync";
import { enqueueOfflinePayment, isNetworkError, isOffline } from "@/lib/offlinePosQueue";
import { getCatIcon, POS_ACTION_STYLES } from "@/lib/brandTheme";
import { resolveMediaUrl } from "@/lib/mediaUrl";
import { billHasAssignedTable, dineInOrder } from "@/lib/posTable";
import { canAccessDashboard } from "@/lib/permissions";

function heldTableCode(held) {
  const code = String(held?.table_code || held?.table || "").trim();
  if (!code || code === "—" || code === "-") return null;
  return code;
}

function heldWhereLabel(held, t) {
  const table = heldTableCode(held);
  if (table) return `${t("pos.tableShort", "Tbl")} ${table}`;
  const type = String(held?.order_type || "").toUpperCase();
  if (type === "TA") return t("pos.takeAway", "Take Away");
  if (type === "DL") return t("pos.delivery", "Delivery");
  if (type === "DI") return t("pos.dineIn", "Dine In");
  return held?.order_type_label || t("pos.takeAway", "Take Away");
}

function heldBillNo(held) {
  return held?.bill_number || (held?.sales_no != null ? `No. ${held.sales_no}` : "Bill");
}

function sanitizeTendered(value, decimals = 2) {
  const raw = String(value ?? "");
  const dot = raw.indexOf(".");
  if (dot === -1) return raw;
  return raw.slice(0, dot + 1 + decimals);
}

const ORDER_TYPE_DEFS = [
  { id: "DI", labelKey: "pos.dineIn", icon: Utensils, key: "F5" },
  { id: "TA", labelKey: "pos.takeAway", icon: ShoppingBag, key: "F6" },
  { id: "DL", labelKey: "pos.delivery", icon: Truck, key: "F7" },
];

/** Active / inactive pill styles per order type */
const ORDER_TYPE_STYLES = {
  DI: {
    active: "bg-[#F36F21] text-white shadow-sm",
    idle: "text-[#F36F21] hover:bg-orange-50",
    key: "bg-orange-600/80 text-white",
  },
  TA: {
    active: "bg-[#2563EB] text-white shadow-sm",
    idle: "text-[#2563EB] hover:bg-blue-50",
    key: "bg-blue-700/80 text-white",
  },
  DL: {
    active: "bg-[#7C3AED] text-white shadow-sm",
    idle: "text-[#7C3AED] hover:bg-violet-50",
    key: "bg-violet-700/80 text-white",
  },
};

/** Fixed 4×4 touch grid — row-major order, all visible, no dropdowns */
const GRID_ACTION_IDS = [
  "hold", "recall", "void", "discount",
  "kotsend", "knote", "parcel", "splitbill",
  "return", "credit", "drawer", "fast",
  "transfertable", "mergetable", "changewaiter", "closeshift",
];

const MODE_CONFIG_DEFS = {
  QTY:    { labelKey: "pos.mode.qty.label", hintKey: "pos.mode.qty.hint", suffix: "", color: "#0EA5E9", textOn: "white" },
  RATE:   { labelKey: "pos.mode.rate.label", hintKey: "pos.mode.rate.hint", suffixKey: "currency", color: "#7C3AED", textOn: "white" },
  DISC:   { labelKey: "pos.mode.disc.label", hintKey: "pos.mode.disc.hint", suffix: "%", color: "#F59E0B", textOn: "black" },
  PARCEL: { labelKey: "pos.mode.parcel.label", hintKey: "pos.mode.parcel.hint", suffixKey: "currency", color: "#0891B2", textOn: "white" },
  SVC:    { labelKey: "pos.mode.svc.label", hintKey: "pos.mode.svc.hint", suffix: "%", color: "#0D9488", textOn: "white" },
  TIP:    { labelKey: "pos.mode.tip.label", hintKey: "pos.mode.tip.hint", suffixKey: "currency", color: "#DB2777", textOn: "white" },
  TABLE:  { labelKey: "pos.mode.table.label", hintKey: "pos.mode.table.hint", suffix: "", color: "#16A34A", textOn: "white" },
  RECALL: { labelKey: "pos.mode.recall.label", hintKey: "pos.mode.recall.hint", suffix: "", color: "#475569", textOn: "white" },
  CREDIT: { labelKey: "pos.mode.credit.label", hintKey: "pos.mode.credit.hint", suffix: "", color: "#DC2626", textOn: "white" },
};

const ACTION_DEFS = [
  { id: "hold", labelKey: "pos.action.hold", key: "F1" },
  { id: "recall", labelKey: "pos.action.recall", key: "F2", mode: "RECALL" },
  { id: "void", labelKey: "pos.action.void", key: "F3" },
  { id: "discount", labelKey: "pos.action.discount", key: "F4", mode: "DISC" },
  { id: "kotsend", labelKey: "pos.action.sendKot" },
  { id: "kotreprint", labelKey: "pos.action.kotReprint", key: "F6" },
  { id: "knote", labelKey: "pos.action.kotNote", key: "F8" },
  { id: "rate", labelKey: "pos.action.chgRate", key: "F9", mode: "RATE" },
  { id: "parcel", labelKey: "pos.action.parcel", key: "F10", mode: "PARCEL" },
  { id: "return", labelKey: "pos.action.return", key: "F11" },
  { id: "credit", labelKey: "pos.action.credit", key: "F12" },
  { id: "fast", labelKey: "pos.action.fastMenu", key: "Ctl F" },
  { id: "combo", labelKey: "pos.action.combo", key: "Ctl C" },
  { id: "mark", labelKey: "pos.action.group", key: "Ctl G" },
  { id: "drawer", labelKey: "pos.action.drawer", key: "Ctl O" },
  { id: "splitbill", label: "SPLIT" },
  { id: "mergetable", label: "MERGE" },
  { id: "transfertable", label: "TRANSFER" },
  { id: "changewaiter", label: "WAITER" },
  { id: "kothistory", label: "KOT HIST" },
  { id: "openbills", label: "OPEN" },
  { id: "pendingorders", label: "PENDING" },
  { id: "closeshift", label: "CLOSE" },
  { id: "clear", labelKey: "pos.action.clear", key: "Ctl X", wide: true },
];

const ACTION_ICONS = {
  hold: Pause,
  recall: RotateCcw,
  void: X,
  discount: Percent,
  kotsend: Send,
  kotreprint: PrinterIcon,
  knote: MessageSquare,
  rate: DollarSign,
  parcel: Package,
  return: Undo2,
  credit: CreditCard,
  fast: Zap,
  combo: Layers,
  mark: Users,
  drawer: Banknote,
  clear: Trash2,
  splitbill: Scissors,
  mergetable: Merge,
  transfertable: ArrowRightLeft,
  changewaiter: UserCog,
  kothistory: ClipboardList,
  openbills: ListOrdered,
  pendingorders: History,
  closeshift: Lock,
};

/** Let delivery/search/etc. inputs receive keystrokes instead of the POS keypad. */
function isPosTextEntryTarget(target) {
  if (!target || !(target instanceof HTMLElement)) return false;
  const tag = target.tagName.toLowerCase();
  if (tag === "input" || tag === "textarea" || tag === "select") return true;
  return target.isContentEditable;
}

export default function POS() {
  const { t } = useTranslation();
  const { isRtl } = useLocale();
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user, refreshUser } = useAuth();
  const { code: currencyCode, decimals: currencyDecimals, formatPlain, formatWithCode, roundOff: calcRoundOff, step: moneyStep } = useCurrency();
  const showBackOffice = canAccessDashboard(user?.permissions || []);
  const branchId = user?.default_branch?.id;
  const counterId = user?.default_counter?.id;
  const counterName = user?.default_counter?.name || "Counter";

  const { data: shiftData, isLoading: shiftLoading, refetch: refetchShift } = useCounterShift(
    branchId,
    counterId,
  );

  const { data: businessDayPayload, isLoading: businessDayLoading, isFetching: businessDayFetching } = useQuery({
    queryKey: ["business-day", branchId],
    queryFn: async () => {
      const { data } = await api.get("/business-day/current", { params: { branch_id: branchId } });
      return data;
    },
    enabled: Boolean(branchId),
    staleTime: 0,
    refetchOnMount: "always",
  });
  const businessDay = businessDayPayload?.business_day;
  const dayIsOpen = businessDay?.status === "open";

  const openDayMutation = useMutation({
    mutationFn: async ({ businessDate, twoFactorCode }) => {
      const payload = { branch_id: branchId, business_date: businessDate };
      if (twoFactorCode) payload.two_factor_code = twoFactorCode;
      const { data } = await api.post("/business-day/open", payload);
      return data;
    },
    onSuccess: async (data) => {
      if (data?.business_day) {
        queryClient.setQueryData(["business-day", branchId], (prev) => ({
          ...(prev && typeof prev === "object" ? prev : {}),
          business_day: data.business_day,
        }));
      }
      await queryClient.invalidateQueries({ queryKey: ["business-day", branchId] });
      toast.success(data?.message || "Business day opened");
    },
  });

  const { pending: offlinePending, syncQueue } = useOfflinePosSync({
    enabled: Boolean(branchId),
    onSynced: (data) => {
      if (data?.new_bill) {
        setBill(data.new_bill);
        setSelectedLine(data.new_bill.lines[0]?.id || null);
      }
    },
  });
  const shiftOpen = Boolean(shiftData?.session);

  const { data: posSettings } = useQuery({
    queryKey: ["pos-settings", branchId],
    queryFn: async () => {
      const { data: res } = await api.get("/pos-settings", { params: { branch_id: branchId } });
      return res.settings;
    },
    enabled: Boolean(branchId),
    staleTime: 60_000,
  });
  const tipsOnPos = posSettings?.enable_tips_on_pos !== false;
  const loyaltyOnPos = posSettings?.enable_loyalty_on_pos !== false;
  const [shiftFloat, setShiftFloat] = useState("0");

  const openShiftMutation = useMutation({
    mutationFn: async () => {
      const { data: res } = await api.post("/counter-sessions/open", {
        branch_id: branchId,
        counter_id: counterId,
        opening_float: Number(shiftFloat) || 0,
      });
      return res;
    },
    onSuccess: async (res) => {
      toast.success(res.message || "Shift opened.");
      await refreshUser();
      await refetchShift();
    },
    onError: (err) => {
      toast.error(err.response?.data?.message || "Could not open shift.");
    },
  });

  const [closeShiftOpen, setCloseShiftOpen] = useState(false);
  const [closingCash, setClosingCash] = useState("");
  const [closeNotes, setCloseNotes] = useState("");

  const closeShiftMutation = useMutation({
    mutationFn: async ({ sessionId, counted, notes }) => {
      const payload = { branch_id: branchId, close_notes: notes || undefined };
      if (counted !== "" && counted != null) {
        payload.closing_cash_counted = Number(counted);
      }
      const { data: res } = await api.post(`/counter-sessions/${sessionId}/close`, payload);
      return res;
    },
    onSuccess: async (res) => {
      const variance = res.summary?.variance;
      toast.success(
        variance != null
          ? `Shift closed. Cash variance: ${aed(variance)}`
          : res.message || "Shift closed.",
      );
      setCloseShiftOpen(false);
      setClosingCash("");
      setCloseNotes("");
      setBill(null);
      setSelectedLine(null);
      await refreshUser();
      await refetchShift();
    },
    onError: (err) => {
      toast.error(err.response?.data?.message || "Could not close shift.");
    },
  });

  const [activeCategory, setActiveCategory] = useState(null);
  const [view, setView] = useState("category");
  const [bill, setBill] = useState(null);
  const [search, setSearch] = useState("");
  const [selectedLine, setSelectedLine] = useState(null);
  const [recentIds, setRecentIds] = useState([]);

  const [mode, setMode] = useState("QTY");
  const [buffer, setBuffer] = useState("");
  const [knoteDialogOpen, setKnoteDialogOpen] = useState(false);
  const [customerDialogOpen, setCustomerDialogOpen] = useState(false);
  const [creditDialogOpen, setCreditDialogOpen] = useState(false);
  const [creditCustomer, setCreditCustomer] = useState(null);
  const [billReprintOpen, setBillReprintOpen] = useState(false);
  const [reprintDateFrom, setReprintDateFrom] = useState(todayYmd);
  const [reprintDateTo, setReprintDateTo] = useState(todayYmd);
  const [reprintSearch, setReprintSearch] = useState("");
  const [reprintBills, setReprintBills] = useState([]);
  const [reprintLoading, setReprintLoading] = useState(false);
  const [customerSearch, setCustomerSearch] = useState("");
  const [knoteSelection, setKnoteSelection] = useState([]);
  const [knoteNewName, setKnoteNewName] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("cash");
  const [fastMenuOpen, setFastMenuOpen] = useState(false);
  const [comboMenuOpen, setComboMenuOpen] = useState(false);
  const [kotReprintOpen, setKotReprintOpen] = useState(false);
  const [returnDialogOpen, setReturnDialogOpen] = useState(false);
  const [returnReference, setReturnReference] = useState("");
  const [returnLookup, setReturnLookup] = useState(null);
  const [returnSelections, setReturnSelections] = useState({});
  const [returnRefundMethod, setReturnRefundMethod] = useState("cash");
  const [keypadOpen, setKeypadOpen] = useState(false);
  const [payOpen, setPayOpen] = useState(false);
  const [tenderedAmount, setTenderedAmount] = useState("");
  const [tenderedDirty, setTenderedDirty] = useState(false);
  const [cashReceiptTrn, setCashReceiptTrn] = useState("");
  const [cashReceiptName, setCashReceiptName] = useState("");
  const [roundOff, setRoundOff] = useState(false);
  const [sizePickItem, setSizePickItem] = useState(null);
  const [bootstrapping, setBootstrapping] = useState(true);
  const [bootError, setBootError] = useState(null);
  const lastPaidBillRef = useRef(null);
  const paymentLockRef = useRef(false);
  const searchInputRef = useRef(null);
  const blockPosKeysRef = useRef(false);
  const reprintFetchRef = useRef(0);
  const customerFetchRef = useRef(0);
  const [customerOptions, setCustomerOptions] = useState([]);
  const [customersLoading, setCustomersLoading] = useState(false);
  const [loyaltyDialogOpen, setLoyaltyDialogOpen] = useState(false);
  const [redeemPoints, setRedeemPoints] = useState("");
  const [loyaltySummary, setLoyaltySummary] = useState(null);
  const [catCollapsed, setCatCollapsed] = useState(() =>
    typeof window !== "undefined" ? window.innerWidth < 1280 : false,
  );
  const [deliveryDialogOpen, setDeliveryDialogOpen] = useState(false);

  useEffect(() => {
    const onResize = () => {
      if (window.innerWidth < 1100) setCatCollapsed(true);
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const { data: kotTicketsData, isFetching: kotTicketsLoading, refetch: refetchKotTickets } = useQuery({
    queryKey: ["bill-kot-tickets", bill?.id, branchId],
    queryFn: async () => {
      const { data } = await api.get(`/bills/${bill.id}/kot-tickets`, { params: { branch_id: branchId } });
      return data;
    },
    enabled: Boolean(branchId && bill?.id && kotReprintOpen),
  });

  const kotReprintMutation = useMutation({
    mutationFn: async (kotTicketIds = null) => {
      const body = kotTicketIds?.length ? { kot_ticket_ids: kotTicketIds } : {};
      const { data } = await api.post(`/bills/${bill.id}/kot/reprint`, body, {
        params: { branch_id: branchId },
      });
      return data;
    },
    onSuccess: (data) => {
      const { printed } = runKotReprint(data.print_kots);
      if (printed > 0) {
        toast.success(data.message || `KOT reprinted (${printed})`);
        refetchKotTickets();
      } else {
        toast.error("KOT reprint failed");
      }
    },
    onError: (err) => {
      toast.error(err.response?.data?.message || err.response?.data?.errors?.bill?.[0] || "KOT reprint failed");
    },
  });

  const posReady = Boolean(branchId && bill?.id);

  const { data: kitchenNoteOptions = [], refetch: refetchKitchenNotes, isFetching: kitchenNotesLoading } = useQuery({
    queryKey: ["kitchen-notes", branchId],
    queryFn: async () => {
      const { data } = await api.get("/kitchen-notes", { params: { branch_id: branchId } });
      return (data.kitchen_notes || []).filter((n) => n.status !== "Inactive" && n.is_active !== false);
    },
    enabled: posReady,
    staleTime: 120_000,
  });

  const { data: fastMenuData } = useQuery({
    queryKey: ["fast-menu-slots", branchId],
    queryFn: async () => {
      const { data } = await api.get("/fast-menu-slots", { params: { branch_id: branchId } });
      return data;
    },
    enabled: posReady,
    staleTime: 120_000,
  });

  const fastMenuEnabled = fastMenuData?.enabled !== false;
  const fastSlotMap = useMemo(() => {
    const map = {};
    (fastMenuData?.slots || []).forEach((slot) => {
      if (slot.menu_item_id) map[slot.slot_key] = slot;
    });
    return map;
  }, [fastMenuData]);

  const { data: menuCombos = [] } = useQuery({
    queryKey: ["menu-combos", branchId],
    queryFn: async () => {
      const { data } = await api.get("/menu-combos", { params: { branch_id: branchId } });
      return data.combos || [];
    },
    enabled: posReady,
    staleTime: 120_000,
  });

  const comboItemIds = useMemo(
    () => new Set(menuCombos.map((c) => c.menu_item_id)),
    [menuCombos],
  );

  const todayStr = useMemo(() => new Date().toISOString().slice(0, 10), []);

  const { data: topItemsReport } = useQuery({
    queryKey: ["pos-top-items", branchId, todayStr],
    queryFn: async () => {
      const { data } = await api.get("/reports/backlog/top-items", {
        params: { branch_id: branchId, from_date: todayStr, to_date: todayStr, limit: 12 },
      });
      return data;
    },
    enabled: posReady,
    staleTime: 120_000,
  });

  const { data: menuItems = [], isLoading: menuLoading } = useQuery({
    queryKey: ["pos-menu-items", branchId],
    queryFn: async () => {
      const { data } = await api.get("/menu-items", {
        params: { branch_id: branchId, show_in_pos: 1 },
      });
      return data.menu_items.filter((m) => m.show_in_pos !== false && m.status === "Active");
    },
    enabled: Boolean(branchId),
  });

  const { data: subMenuList = [] } = useQuery({
    queryKey: ["sub-menus", branchId],
    queryFn: async () => {
      const { data } = await api.get("/sub-menus", { params: { branch_id: branchId } });
      return data.sub_menus || [];
    },
    enabled: Boolean(branchId),
    staleTime: 120_000,
  });

  const categoryImages = useMemo(
    () => Object.fromEntries(subMenuList.map((s) => [s.name, s.image_url]).filter(([name]) => name)),
    [subMenuList],
  );

  const categories = useMemo(() => {
    const counts = menuItems.reduce((acc, item) => {
      const cat = item.category || "Other";
      acc[cat] = (acc[cat] || 0) + 1;
      return acc;
    }, {});
    return Object.entries(counts).map(([name, count], idx) => ({
      id: `c${idx + 1}`,
      name,
      count,
    }));
  }, [menuItems]);

  useEffect(() => {
    if (!categories.length) return;
    if (!activeCategory || !categories.some((c) => c.name === activeCategory)) {
      setActiveCategory(categories[0].name);
    }
  }, [categories, activeCategory]);

  const createBillMutation = useMutation({
    mutationFn: async (payload = {}) => {
      const { data } = await api.post("/bills", payload, {
        params: { branch_id: branchId, counter_id: counterId },
      });
      return data.bill;
    },
    onSuccess: (newBill) => {
      billRef.current = newBill;
      setBill(newBill);
      setSelectedLine(newBill.lines[0]?.id || null);
      setMode("QTY");
      setBuffer("");
      queryClient.invalidateQueries({ queryKey: ["pos-session-nav", branchId, counterId] });
      toast.success(`${newBill.bill_number} · new bill`);
    },
    onError: () => toast.error("Failed to create bill"),
  });

  const updateBillMutation = useMutation({
    mutationFn: async (payload) => {
      const { data } = await api.put(`/bills/${bill.id}`, payload, {
        params: { branch_id: branchId },
      });
      return data.bill;
    },
    onSuccess: (updated) => {
      setBill(updated);
      if (!updated.lines.some((l) => l.id === selectedLine)) {
        setSelectedLine(updated.lines[0]?.id || null);
      }
    },
    onError: (err) => {
      const errors = err.response?.data?.errors;
      const msg = errors
        ? Object.values(errors).flat()[0]
        : err.response?.data?.message || "Failed to update bill";
      toast.error(msg);
    },
  });

  const pricingMutation = useMutation({
    mutationFn: async ({ lineId, rate }) => {
      const { data } = await api.post(
        `/bills/${bill.id}/lines/${lineId}/rate`,
        { rate },
        { params: { branch_id: branchId } },
      );
      return data;
    },
    onSuccess: (data, vars) => {
      setBill(data.bill);
      toast.success(`Rate updated to ${formatPlain(vars.rate)}`);
    },
    onError: (err) => {
      const msg = err.response?.data?.message
        || err.response?.data?.errors?.rate?.[0]
        || "Failed to change rate";
      toast.error(msg);
    },
  });

  const kitchenNoteMutation = useMutation({
    mutationFn: async ({ lineId, kitchen_note_ids }) => {
      const { data } = await api.put(
        `/bills/${bill.id}/lines/${lineId}/kitchen-notes`,
        { kitchen_note_ids },
        { params: { branch_id: branchId } },
      );
      return data;
    },
    onSuccess: (data) => {
      setBill(data.bill);
      setKnoteDialogOpen(false);
      toast.success("Kitchen notes saved");
    },
    onError: (err) => {
      const msg = err.response?.data?.message
        || err.response?.data?.errors?.kitchen_note_ids?.[0]
        || "Failed to save kitchen notes";
      toast.error(msg);
    },
  });

  const createKitchenNoteMutation = useMutation({
    mutationFn: async (name) => {
      const { data } = await api.post(
        "/kitchen-notes",
        { name, group: "General", status: "Active", is_active: true },
        { params: { branch_id: branchId } },
      );
      return data.kitchen_note;
    },
    onSuccess: async (note) => {
      setKnoteNewName("");
      setKnoteSelection((prev) => (prev.includes(note.id) ? prev : [...prev, note.id]));
      await queryClient.invalidateQueries({ queryKey: ["kitchen-notes", branchId] });
      toast.success(`${note.name} added`);
    },
    onError: (err) => {
      const msg =
        err.response?.data?.message
        || err.response?.data?.errors?.name?.[0]
        || err.response?.data?.errors?.code?.[0]
        || "Failed to save kitchen note";
      toast.error(msg);
    },
  });

  const holdBillMutation = useMutation({
    mutationFn: async () => {
      const { data } = await api.post(
        `/bills/${bill.id}/hold`,
        {},
        { params: { branch_id: branchId } },
      );
      return data;
    },
    onSuccess: (data) => {
      setBill(data.bill);
      setSelectedLine(data.bill.lines[0]?.id || null);
      setMode("QTY");
      setBuffer("");
      queryClient.invalidateQueries({ queryKey: ["held-bills", branchId] });
      toast.success(`Bill ${data.held_bill.bill_number} held — new bill started`);
    },
    onError: (err) => {
      const msg = err.response?.data?.message
        || err.response?.data?.errors?.bill?.[0]
        || "Failed to hold bill";
      toast.error(msg);
    },
  });

  const recallBillMutation = useMutation({
    mutationFn: async (reference) => {
      const { data } = await api.post(
        "/bills/payable-lookup",
        { reference: String(reference).trim() },
        { params: { branch_id: branchId } },
      );
      return data;
    },
    onSuccess: (data) => {
      setBill(data.bill);
      setSelectedLine(data.bill.lines[0]?.id || null);
      setMode("QTY");
      setBuffer("");
      const table = data.bill.table_code;
      const tableLabel =
        table && table !== "—" && table !== "-" ? ` · ${table}` : "";
      toast.success(`${data.bill.bill_number}${tableLabel} — ready to pay`);
    },
    onError: (err) => {
      const msg = err.response?.data?.message
        || err.response?.data?.errors?.reference?.[0]
        || "Bill not found — check number on guest slip";
      toast.error(msg);
    },
  });

  const drawerMutation = useMutation({
    mutationFn: async () => {
      const { data } = await api.post(
        "/drawer/open",
        { reason: "manual" },
        { params: { branch_id: branchId } },
      );
      return data;
    },
    onSuccess: () => toast.success("Cash drawer opened"),
    onError: (err) => {
      toast.error(err.response?.data?.message || err.response?.data?.errors?.printer?.[0] || "Failed to open drawer");
    },
  });

  const printBillMutation = useMutation({
    mutationFn: async () => {
      const { data } = await api.post(
        `/bills/${bill.id}/print`,
        {},
        { params: { branch_id: branchId } },
      );
      return data;
    },
    onSuccess: (data) => {
      if (data.print_receipt && printBillReceipt(data.print_receipt)) {
        toast.success(data.print_receipt.dispatch_message || data.message || "Guest bill sent to print");
      } else {
        cancelPreparedBillPrint();
        toast.error("Print data ready but browser print did not open. Check popup/print blockers.");
      }
    },
    onError: (err) => {
      cancelPreparedBillPrint();
      toast.error(
        err.response?.data?.message
        || err.response?.data?.errors?.bill?.[0]
        || err.response?.data?.errors?.printer?.[0]
        || "Print failed",
      );
    },
  });

  const reprintBillMutation = useMutation({
    mutationFn: async (billId) => {
      const { data } = await api.post(
        `/bills/${billId}/reprint`,
        {},
        { params: { branch_id: branchId } },
      );
      return data;
    },
    onSuccess: (data) => {
      if (data.print_receipt && printBillReceipt(data.print_receipt)) {
        toast.success(data.print_receipt.dispatch_message || `Reprint ${data.bill.bill_number}`);
        setBillReprintOpen(false);
      } else {
        cancelPreparedBillPrint();
      }
    },
    onError: (err) => {
      cancelPreparedBillPrint();
      toast.error(err.response?.data?.message || "Reprint failed");
    },
  });

  const loadReprintBills = useCallback(async (from, to, search = "") => {
    if (!branchId) return;
    const fetchId = ++reprintFetchRef.current;
    setReprintLoading(true);
    try {
      const params = { branch_id: branchId, date_from: from, date_to: to, limit: 50 };
      if (search) params.search = search;
      const { data } = await api.get("/bills/history", { params });
      if (fetchId !== reprintFetchRef.current) return;
      setReprintBills(data.bills ?? []);
    } catch (err) {
      if (fetchId !== reprintFetchRef.current) return;
      setReprintBills([]);
      toast.error(err.response?.data?.message || "Failed to load bills");
    } finally {
      if (fetchId === reprintFetchRef.current) {
        setReprintLoading(false);
      }
    }
  }, [branchId]);

  function openBillReprint() {
    const day = todayYmd();
    setReprintDateFrom(day);
    setReprintDateTo(day);
    setReprintSearch("");
    setReprintBills([]);
    setBillReprintOpen(true);
  }

  const paymentMutation = useMutation({
    mutationFn: async ({ method, tendered, customer_name, customer_tax_number, round_off, customerId }) => {
      const base = `/bills/${bill.id}/payments`;
      const roundPayload = { round_off: Boolean(round_off) };
      if (method === "cash") {
        const { data } = await api.post(
          `${base}/cash`,
          {
            tendered_amount: tendered,
            customer_name: customer_name || bill.customer_name,
            customer_tax_number: customer_tax_number || null,
            ...roundPayload,
          },
          { params: { branch_id: branchId } },
        );
        return data;
      }
      if (method === "card") {
        const { data } = await api.post(
          `${base}/card`,
          {
            customer_name: customer_name || bill.customer_name,
            customer_tax_number: customer_tax_number || null,
            ...roundPayload,
          },
          { params: { branch_id: branchId } },
        );
        return data;
      }
      if (method === "split") {
        const baseDue = Number(bill.net_amount ?? 0);
        const dueAmt = round_off
          ? Number(formatPlain(baseDue + calcRoundOff(baseDue)))
          : baseDue;
        const cashAmt = Math.round(Number(tendered) * 100) / 100;
        const cardAmt = Math.round((dueAmt - cashAmt) * 100) / 100;
        const { data } = await api.post(
          `${base}/mixed`,
          {
            customer_name: customer_name || bill.customer_name,
            customer_tax_number: customer_tax_number || null,
            ...roundPayload,
            payments: [
              { method: "cash", amount: cashAmt, tendered_amount: cashAmt },
              { method: "card", amount: cardAmt },
            ],
          },
          { params: { branch_id: branchId } },
        );
        return data;
      }
      if (method === "credit") {
        const { data } = await api.post(
          `${base}/credit`,
          { customer_id: customerId || bill.customer_id || undefined, ...roundPayload },
          { params: { branch_id: branchId } },
        );
        return data;
      }
      const { data } = await api.post(
        `${base}/retailer`,
        { customer_id: bill.customer_id || undefined, ...roundPayload },
        { params: { branch_id: branchId } },
      );
      return data;
    },
    onSuccess: (data) => {
      paymentLockRef.current = false;
      lastPaidBillRef.current = data.bill;
      setPayOpen(false);
      setCreditDialogOpen(false);
      setCreditCustomer(null);
      setTenderedAmount("");
      setTenderedDirty(false);
      setCashReceiptTrn("");
      setCashReceiptName("");
      setRoundOff(false);
      const change = Number(data.change_amount || 0);
      let printed = false;
      if (data.print_receipt) {
        printed = printBillReceipt(data.print_receipt);
      } else {
        cancelPreparedBillPrint();
      }
      const drawerNote = data.drawer_kick ? " · drawer opened" : "";
      if (change > 0) {
        toast.success(`Paid — change ${formatPlain(change)} ${currencyCode}${printed ? " · receipt printed" : ""}${drawerNote}`);
      } else {
        toast.success(`${data.message || "Payment completed"}${printed ? " · receipt printed" : ""}${drawerNote}`);
      }
      if (data.new_bill?.id) {
        setBill(data.new_bill);
        setSelectedLine(data.new_bill.lines?.[0]?.id || null);
        setMode("QTY");
        setBuffer("");
        queryClient.invalidateQueries({ queryKey: ["pos-session-nav", branchId, counterId] });
      } else {
        // Server paid OK but next-bill create failed — open a fresh bill.
        createBillMutation.mutate({
          order_type: data.bill?.order_type || bill?.order_type || "DI",
          customer_name: "Walk-in",
        });
      }
    },
    onError: (err, vars) => {
      paymentLockRef.current = false;
      cancelPreparedBillPrint();
      if (isOffline() || isNetworkError(err)) {
        enqueueOfflinePayment({
          billId: bill?.id,
          branchId,
          method: vars.method,
          tendered: vars.tendered,
          customerId: bill?.customer_id,
        });
        toast.warning("Offline — payment queued. Will sync when connection returns.");
        return;
      }
      const errors = err.response?.data?.errors;
      const msg = err.response?.data?.message
        || errors?.tendered_amount?.[0]
        || errors?.customer_id?.[0]
        || errors?.bill?.[0]
        || errors?.delivery_contact?.[0]
        || errors?.delivery_address?.[0]
        || errors?.delivery_area_id?.[0]
        || (errors ? Object.values(errors).flat()[0] : null)
        || "Payment failed";
      const alreadyPaid = /already paid/i.test(String(msg || ""));
      if (alreadyPaid) {
        // Prior attempt paid on server but returned 500 — recover POS session.
        toast.success("Payment already recorded — starting new bill");
        lastPaidBillRef.current = bill;
        setPayOpen(false);
        createBillMutation.mutate({
          order_type: bill?.order_type || "DI",
          customer_name: "Walk-in",
        });
        return;
      }
      toast.error(msg);
    },
  });

  const chargeMutation = useMutation({
    mutationFn: async ({ type, value }) => {
      if (type === "parcel") {
        const { data } = await api.post(
          `/bills/${bill.id}/parcel`,
          { parcel_charge: value },
          { params: { branch_id: branchId } },
        );
        return data;
      }
      if (type === "tip") {
        const { data } = await api.post(
          `/bills/${bill.id}/tip`,
          { tip_amount: value },
          { params: { branch_id: branchId } },
        );
        return data;
      }
      const { data } = await api.post(
        `/bills/${bill.id}/service-charge`,
        { service_charge_percent: value },
        { params: { branch_id: branchId } },
      );
      return data;
    },
    onSuccess: (data, vars) => {
      setBill(data.bill);
      if (vars.type === "parcel") {
        toast.success(`Parcel charge ${formatPlain(vars.value)} applied`);
      } else if (vars.type === "tip") {
        toast.success(`Tip ${formatPlain(vars.value)} ${currencyCode} applied`);
      } else {
        toast.success(`Service charge ${Number(vars.value).toFixed(2)}% applied`);
      }
    },
    onError: (err) => {
      const msg = err.response?.data?.message
        || err.response?.data?.errors?.parcel_charge?.[0]
        || err.response?.data?.errors?.service_charge_percent?.[0]
        || err.response?.data?.errors?.tip?.[0]
        || "Failed to apply charge";
      toast.error(msg);
    },
  });

  const discountMutation = useMutation({
    mutationFn: async ({ scope, lineId, discount_percent }) => {
      if (scope === "line") {
        const { data } = await api.post(
          `/bills/${bill.id}/lines/${lineId}/discount`,
          { discount_percent },
          { params: { branch_id: branchId } },
        );
        return data;
      }
      const { data } = await api.post(
        `/bills/${bill.id}/discount`,
        { discount_percent },
        { params: { branch_id: branchId } },
      );
      return data;
    },
    onSuccess: (data, vars) => {
      setBill(data.bill);
      const pct = vars.discount_percent;
      if (vars.scope === "line") {
        toast.success(`Item discount ${pct}% applied`);
      } else {
        toast.success(`Bill discount ${pct}% applied`);
      }
    },
    onError: (err) => {
      const msg = err.response?.data?.message
        || err.response?.data?.errors?.discount_percent?.[0]
        || "Failed to apply discount";
      toast.error(msg);
    },
  });

  const returnLookupMutation = useMutation({
    mutationFn: async (reference) => {
      const { data } = await api.get("/sales-returns/lookup", {
        params: { branch_id: branchId, reference: String(reference).trim() },
      });
      return data;
    },
    onSuccess: (data) => {
      setReturnLookup(data);
      setReturnRefundMethod(data.suggested_refund_method || "cash");
      const initial = {};
      (data.lines || []).forEach((line) => {
        initial[line.sales_line_id] = 0;
      });
      setReturnSelections(initial);
    },
    onError: (err) => {
      setReturnLookup(null);
      const msg = err.response?.data?.message
        || err.response?.data?.errors?.reference?.[0]
        || "Bill not found";
      toast.error(msg);
    },
  });

  const returnSubmitMutation = useMutation({
    mutationFn: async () => {
      const lines = Object.entries(returnSelections)
        .filter(([, qty]) => Number(qty) > 0)
        .map(([sales_line_id, qty]) => ({
          sales_line_id: Number(sales_line_id),
          qty: Number(qty),
        }));
      const { data } = await api.post(
        "/sales-returns",
        {
          reference: returnReference.trim(),
          refund_method: returnRefundMethod,
          lines,
        },
        { params: { branch_id: branchId } },
      );
      return data;
    },
    onSuccess: (data) => {
      toast.success(`Return ${data.sales_return.return_number} — refund ${aed(data.sales_return.net_amount)}`);
      setReturnDialogOpen(false);
      setReturnReference("");
      setReturnLookup(null);
      setReturnSelections({});
    },
    onError: (err) => {
      const msg = err.response?.data?.message
        || err.response?.data?.errors?.lines?.[0]
        || err.response?.data?.errors?.refund_method?.[0]
        || "Failed to process return";
      toast.error(msg);
    },
  });

  const notifyKotJobs = useCallback((data, { silentIfEmpty = true } = {}) => {
    const jobs = data?.kot_jobs || [];
    if (!jobs.length) {
      if (!silentIfEmpty) toast.info("No kitchen tickets to send");
      return;
    }

    dispatchKotJobsLan(jobs);

    const printers = [...new Set(jobs.map((j) => j.printer?.name).filter(Boolean))].join(", ");
    const stations = [...new Set(jobs.map((j) => j.station).filter(Boolean))].join(", ");
    if (printers) {
      toast.success(`KOT sent → ${printers}`);
    } else if (stations) {
      toast.success(`KOT sent — ${stations}`);
    } else {
      toast.success("KOT sent to kitchen");
    }
  }, []);

  const sendKotMutation = useMutation({
    mutationFn: async () => {
      const { data } = await api.post(
        `/bills/${bill.id}/kot/send`,
        {},
        { params: { branch_id: branchId } },
      );
      return data;
    },
    onSuccess: (data) => notifyKotJobs(data, { silentIfEmpty: false }),
    onError: (err) => {
      const msg = err.response?.data?.message
        || err.response?.data?.errors?.table?.[0]
        || err.response?.data?.errors?.bill?.[0]
        || "Failed to send KOT";
      toast.error(msg);
      if (dineInOrder(bill) && !billHasAssignedTable(bill)) {
        setOpMode("TABLE");
      }
    },
  });

  const comboMutation = useMutation({
    mutationFn: async ({ menuItemId, qty = 1 }) => {
      const { data } = await api.post(
        `/bills/${bill.id}/combos`,
        { menu_item_id: menuItemId, qty },
        { params: { branch_id: branchId } },
      );
      return data;
    },
    onSuccess: (data, vars) => {
      setBill(data.bill);
      const added = data.bill.lines.find(
        (l) => l.menu_item_id === vars.menuItemId && l.is_combo_header,
      );
      setSelectedLine(added?.id || data.bill.lines[data.bill.lines.length - 1]?.id || null);
      const combo = menuCombos.find((c) => c.menu_item_id === vars.menuItemId);
      toast.success(`${combo?.name || "Combo"} added`);
      notifyKotJobs(data);
      setComboMenuOpen(false);
    },
    onError: (err) => {
      const tableErr = err.response?.data?.errors?.table?.[0];
      const msg = tableErr
        || err.response?.data?.message
        || err.response?.data?.errors?.menu_item_id?.[0]
        || "Failed to add combo";
      toast.error(msg);
      if (tableErr && dineInOrder(bill)) {
        setOpMode("TABLE");
      }
    },
  });

  const lineMutation = useMutation({
    mutationFn: async ({ action, lineId, payload, lineName }) => {
      if (action === "add") {
        const { data } = await api.post(`/bills/${bill.id}/lines`, payload, {
          params: { branch_id: branchId },
        });
        return { bill: data.bill, lineName, kot_jobs: data.kot_jobs };
      }
      if (action === "void") {
        const { data } = await api.post(`/bills/${bill.id}/lines/${lineId}/void`, {}, {
          params: { branch_id: branchId },
        });
        return { bill: data.bill, lineName, kot_jobs: data.kot_jobs };
      }
      const { data } = await api.patch(`/bills/${bill.id}/lines/${lineId}`, payload, {
        params: { branch_id: branchId },
      });
      return { bill: data.bill, lineName, kot_jobs: data.kot_jobs };
    },
    onSuccess: ({ bill: updated, lineName, kot_jobs }, vars) => {
      setBill(updated);
      if (vars.action === "add" && vars.payload?.menu_item_id) {
        const added = findStandaloneBillLine(updated.lines, vars.payload.menu_item_id);
        setSelectedLine(added?.id || updated.lines[updated.lines.length - 1]?.id || null);
      } else if (!updated.lines.some((l) => l.id === selectedLine)) {
        setSelectedLine(updated.lines[0]?.id || null);
      }
      if (vars.action === "void" && lineName) {
        toast.success(`${lineName} voided`);
      }
      notifyKotJobs({ kot_jobs });
    },
    onError: (err, vars) => {
      const tableErr = err.response?.data?.errors?.table?.[0];
      const msg = tableErr
        || err.response?.data?.message
        || (vars.action === "void" ? "Failed to void line" : "Failed to update bill line");
      toast.error(msg);
      if (tableErr && dineInOrder(billRef.current)) {
        setOpMode("TABLE");
      }
    },
  });

  const startNewBill = useCallback(
    (overrides = {}) => {
      if (!branchId || !counterId) return;
      createBillMutation.mutate({
        order_type: overrides.order_type || bill?.order_type || "DI",
        table_code: overrides.table_code ?? bill?.table_code ?? null,
        restaurant_table_id: overrides.restaurant_table_id ?? bill?.restaurant_table_id ?? null,
        customer_name: overrides.customer_name || bill?.customer_name || "Walk-in",
      });
    },
    [branchId, counterId, bill, createBillMutation],
  );

  const billRef = useRef(null);
  billRef.current = bill;
  const bootDoneRef = useRef(false);
  const bootScopeRef = useRef("");

  const visitStateRef = useRef({ key: null, state: null });
  if (visitStateRef.current.key !== location.key) {
    visitStateRef.current = { key: location.key, state: location.state || {} };
  }

  const applyLoadedBill = useCallback((loaded, { toastMessage } = {}) => {
    billRef.current = loaded;
    setBill(loaded);
    setSelectedLine(loaded.lines[0]?.id || null);
    setMode("QTY");
    setBuffer("");
    if (toastMessage) toast.success(toastMessage);
  }, []);

  const { data: heldBillsData } = useQuery({
    queryKey: ["held-bills", branchId],
    queryFn: async () => {
      const { data: res } = await api.get("/held-bills", { params: { branch_id: branchId } });
      return res;
    },
    enabled: Boolean(branchId && shiftOpen),
    refetchInterval: 15000,
  });
  const heldBills = heldBillsData?.held_bills ?? [];

  const { data: sessionNav } = useQuery({
    queryKey: ["pos-session-nav", branchId, counterId, bill?.id],
    queryFn: async () => {
      const { data } = await api.get("/bills/session-nav", {
        params: { branch_id: branchId, counter_id: counterId, current_id: bill.id },
      });
      return data;
    },
    enabled: Boolean(branchId && counterId && bill?.id && shiftOpen),
  });

  const sessionNavMutation = useMutation({
    mutationFn: async (direction) => {
      const currentId = billRef.current?.id;
      if (!currentId) {
        throw new Error("No bill loaded");
      }
      const { data } = await api.get("/bills/session-nav", {
        params: {
          branch_id: branchId,
          counter_id: counterId,
          current_id: currentId,
          direction,
        },
      });
      return data;
    },
    onSuccess: (data) => {
      const paid = data.bill.status === "paid" || data.bill.status === "partial_return";
      const openBiz = businessDay?.business_date;
      const billBiz = data.bill.business_date || (data.bill.paid_at || data.bill.created_at || "").toString().slice(0, 10);
      const otherDay = Boolean(openBiz && billBiz && billBiz !== openBiz);
      applyLoadedBill(data.bill, {
        toastMessage: data.recalled
          ? `${data.bill.bill_number} recalled`
          : paid
            ? `${data.bill.bill_number} · paid (view only)`
            : otherDay
              ? `${data.bill.bill_number} · ${billBiz} (view only)`
              : `${data.bill.bill_number} loaded`,
      });
      queryClient.invalidateQueries({ queryKey: ["held-bills", branchId] });
      queryClient.setQueryData(["pos-session-nav", branchId, counterId, data.bill.id], {
        has_prev: data.has_prev,
        has_next: data.has_next,
      });
    },
    onError: (err) => {
      toast.error(err.response?.data?.message || err.message || "No more bills");
    },
  });

  const openHeldBillMutation = useMutation({
    mutationFn: async (held) => {
      const current = billRef.current;
      if (
        current?.id &&
        current.id !== held.id &&
        (current.lines?.length || 0) > 0 &&
        current.status === "open"
      ) {
        await api.post(`/bills/${current.id}/hold`, {}, { params: { branch_id: branchId } });
      }
      const { data } = await api.post(
        `/bills/${held.id}/recall`,
        {},
        { params: { branch_id: branchId } },
      );
      return data;
    },
    onSuccess: (data) => {
      applyLoadedBill(data.bill, {
        toastMessage: `Opened ${data.bill.bill_number}`,
      });
      queryClient.invalidateQueries({ queryKey: ["held-bills", branchId] });
    },
    onError: (err) => {
      toast.error(err.response?.data?.message || err.response?.data?.errors?.bill?.[0] || "Failed to open held bill");
    },
  });

  useEffect(() => {
    return () => {
      bootDoneRef.current = false;
      bootScopeRef.current = "";
    };
  }, []);

  useEffect(() => {
    if (!shiftOpen) {
      billRef.current = null;
      setBill(null);
      bootDoneRef.current = false;
      setBootstrapping(false);
    }
  }, [shiftOpen, counterId]);

  useEffect(() => {
    if (!dayIsOpen) {
      setBootError(null);
      bootDoneRef.current = false;
      billRef.current = null;
      setBill(null);
      setBootstrapping(false);
      return;
    }
    // Clear stale bootstrap errors from attempts while the day was closed.
    setBootError(null);
    bootDoneRef.current = false;
  }, [dayIsOpen]);

  useEffect(() => {
    if (!branchId || !counterId || !shiftOpen || !dayIsOpen) {
      setBootstrapping(false);
      return;
    }

    const scope = `${branchId}:${counterId}`;
    if (bootScopeRef.current !== scope) {
      bootScopeRef.current = scope;
      bootDoneRef.current = false;
    }

    const state = visitStateRef.current.state || {};
    const hasIncomingBill = Boolean(state.recalledBill || state.openBillId || state.openTable);

    if ((bootDoneRef.current || billRef.current) && !hasIncomingBill) {
      setBootstrapping(false);
      return;
    }
    const bootKey = [
      branchId,
      counterId,
      location.key,
      state.recalledBill?.id ?? "",
      state.openBillId ?? "",
      state.recallIfHeld ? "1" : "0",
      state.openTable?.restaurant_table_id ?? "",
    ].join(":");

    let cancelled = false;
    setBootstrapping(true);
    setBootError(null);

    runPosBootstrap(bootKey, async () => {
      if (state.recalledBill) {
        return { bill: state.recalledBill };
      }

      if (state.openBillId) {
        let loaded;
        if (state.recallIfHeld) {
          const { data: res } = await api.post(
            `/bills/${state.openBillId}/recall`,
            {},
            { params: { branch_id: branchId } },
          );
          loaded = res.bill;
        } else {
          const { data: res } = await api.get(`/bills/${state.openBillId}`, {
            params: { branch_id: branchId },
          });
          loaded = res.bill;
        }
        const tableLabel = state.tableCode || loaded.table_code;
        const toastMessage =
          tableLabel && tableLabel !== "—" && tableLabel !== "-"
            ? `Opened ${tableLabel} order`
            : `Opened bill ${loaded.bill_number}`;
        return { bill: loaded, toastMessage };
      }

      if (state.openTable) {
        const { data } = await api.post(
          "/bills",
          {
            order_type: "DI",
            table_code: state.openTable.table_code,
            restaurant_table_id: state.openTable.restaurant_table_id,
          },
          { params: { branch_id: branchId, counter_id: counterId } },
        );
        const label = state.openTable.table_name || state.openTable.table_code;
        return { bill: data.bill, toastMessage: `New order on ${label}` };
      }

      const { data } = await api.post(
        "/bills",
        { order_type: "DI" },
        { params: { branch_id: branchId, counter_id: counterId } },
      );
      return { bill: data.bill };
    })
      .then((result) => {
        if (cancelled || !result?.bill) return;
        bootDoneRef.current = true;
        applyLoadedBill(result.bill, { toastMessage: result.toastMessage });
        setBootError(null);
        navigate("/pos", { replace: true, state: {} });
      })
      .catch((err) => {
        if (cancelled) return;
        const msg = err.response?.data?.message || err.response?.data?.errors?.bill?.[0] || "Failed to open POS";
        setBootError(msg);
        toast.error(msg);
      })
      .finally(() => {
        if (!cancelled) setBootstrapping(false);
      });

    return () => {
      cancelled = true;
    };
  }, [branchId, counterId, shiftOpen, dayIsOpen, location.key, navigate, applyLoadedBill]);

  const items = bill?.lines ?? [];
  const openBusinessDate = businessDay?.business_date || null;
  const billBusinessDate = bill?.business_date
    ? String(bill.business_date).slice(0, 10)
    : null;
  // Open/held bills without a stamped business_date belong to the current open day (server stamps on save/pay).
  const billOnOpenDay = Boolean(
    openBusinessDate
    && (
      !billBusinessDate
        ? (bill?.status === "open" || bill?.status === "held")
        : billBusinessDate === openBusinessDate
    ),
  );
  const billEditable =
    (bill?.status === "open" || bill?.status === "held") && billOnOpenDay;
  const billViewOnly = Boolean(bill) && !billEditable;
  const canGoNext =
    Boolean(sessionNav?.has_next)
    || (Boolean(bill) && !(billEditable && items.length === 0));

  const goSessionPrev = () => {
    if (!sessionNav?.has_prev || sessionNavMutation.isPending) return;
    sessionNavMutation.mutate("prev");
  };

  const goSessionNext = () => {
    if (sessionNavMutation.isPending || createBillMutation.isPending) return;
    if (sessionNav?.has_next) {
      sessionNavMutation.mutate("next");
      return;
    }
    // End of chain while viewing a paid/older bill — open a fresh sale (mobile Tax Invoice).
    if (billEditable && items.length === 0) {
      toast.info("Already on the latest open bill");
      return;
    }
    createBillMutation.mutate({
      order_type: "DI",
      table_code: null,
      restaurant_table_id: null,
      customer_name: "Walk-in",
    });
  };

  const openCloseShift = useCallback(async () => {
    if (!shiftData?.session?.id) {
      toast.error("No open shift to close.");
      return;
    }
    if (items.length > 0) {
      toast.error("Clear or hold the current bill before closing the shift.");
      return;
    }
    setClosingCash("");
    setCloseNotes("");
    setCloseShiftOpen(true);
    await refetchShift();
  }, [shiftData?.session?.id, items.length, refetchShift]);
  const billDisplayRows = useMemo(() => annotateBillLinesForDisplay(items), [items]);
  const orderType = bill?.order_type || "DI";
  const discountPct = bill?.discount_percent ?? 0;
  const itemDiscountTotal = bill?.item_discount_total ?? 0;
  const parcelCharge = bill?.parcel_charge ?? 0;
  const serviceChargeAmt = bill?.service_charge_amount ?? 0;
  const tipAmount = Number(bill?.tip_amount || 0);
  const tableNo = bill?.table_code || "—";

  const recentItems = useMemo(
    () => recentIds.map((id) => menuItems.find((m) => m.id === id)).filter(Boolean),
    [recentIds, menuItems],
  );
  const topToday = useMemo(() => {
    const rows = topItemsReport?.rows || [];
    if (rows.length) {
      return rows
        .map((row) => {
          const match = menuItems.find((m) => m.id === row.menu_item_id);
          if (match) return match;
          return {
            id: row.menu_item_id,
            name: row.item_name,
            name_ar: row.item_name_ar,
            code: row.item_code,
            price: row.net_sales / Math.max(row.qty, 1),
            unit: "EA",
            category: "Other",
          };
        })
        .filter(Boolean)
        .slice(0, 12);
    }
    return recentItems.slice(0, 12);
  }, [topItemsReport, menuItems, recentItems]);

  const visible = useMemo(() => {
    if (search.trim()) {
      const q = search.toLowerCase();
      return menuItems.filter(
        (m) =>
          m.name?.toLowerCase().includes(q) ||
          m.name_ar?.includes(search.trim()) ||
          m.code?.toLowerCase().includes(q),
      );
    }
    if (view === "recent") return recentItems;
    if (view === "top") return topToday;
    return menuItems.filter((m) => (m.category || "Other") === activeCategory);
  }, [activeCategory, search, view, recentItems, topToday, menuItems]);

  const subtotal = bill?.subtotal ?? items.reduce((s, i) => s + i.qty * i.rate, 0);
  const discountAmt = bill?.discount_amount ?? (subtotal * discountPct) / 100;
  const loyaltyDiscount = Number(bill?.loyalty_discount_amount || 0);
  const taxable = bill?.taxable_amount ?? subtotal - discountAmt + parcelCharge + serviceChargeAmt;
  const vat = bill?.vat_amount ?? taxable * 0.05;
  const total = bill?.net_amount ?? taxable + vat;
  const roundOffAmt = Number(bill?.round_off || 0);
  const itemCount = items.length;
  const qtyTotal = items.reduce((s, i) => s + Number(i.qty || 0), 0);
  const appliedDiscount = itemDiscountTotal + discountAmt;
  const selectedItem = items.find((i) => i.id === selectedLine);

  const returnRefundPreview = useMemo(() => {
    if (!returnLookup?.lines) return 0;
    const subtotal = returnLookup.lines.reduce((sum, line) => {
      const qty = Number(returnSelections[line.sales_line_id] || 0);
      return sum + line.unit_amount * qty;
    }, 0);
    const discPct = returnLookup.bill?.discount_percent || 0;
    const afterDisc = subtotal - subtotal * (discPct / 100);
    const taxableReturn = Math.max(0, afterDisc);
    return taxableReturn + taxableReturn * 0.05;
  }, [returnLookup, returnSelections]);

  const saveDeliveryDetails = useCallback(
    (payload) => {
      updateBillMutation.mutate(payload, {
        onSuccess: () => toast.success("Delivery details saved"),
      });
    },
    [updateBillMutation],
  );

  const assignDriverMutation = useMutation({
    mutationFn: async (deliveryPersonId = null) => {
      const body = deliveryPersonId ? { delivery_person_id: deliveryPersonId } : {};
      const { data } = await api.post(`/bills/${bill.id}/assign-driver`, body, {
        params: { branch_id: branchId },
      });
      return data.bill;
    },
    onSuccess: (updated) => {
      setBill(updated);
      toast.success(`Driver assigned: ${updated.delivery_person_name}`);
    },
    onError: (err) => {
      const msg =
        err.response?.data?.errors?.delivery_person_id?.[0]
        || err.response?.data?.message
        || "Driver assignment failed";
      toast.error(msg);
    },
  });

  const processPayment = useCallback(
    (method = paymentMethod, tenderedOverride = undefined, extra = {}) => {
      if (!bill || paymentMutation.isPending || paymentLockRef.current) return;
      if (!items.length) {
        toast.error("Add items before payment");
        return;
      }
      if (bill.status === "paid") {
        toast.info("This bill is already paid.");
        setPayOpen(false);
        return;
      }
      if (bill.order_type === "DL") {
        const hasAddress =
          bill.delivery_address ||
          (bill.delivery_building_no && bill.delivery_flat_no);
        if (!bill.delivery_contact || !hasAddress || !bill.delivery_area_id) {
          toast.error("Complete delivery details (phone, area, bldg+flat or landmark) before payment");
          return;
        }
        if (bill.delivery_charge_preview && !bill.delivery_charge_preview.meets_min_order) {
          toast.error(
            `Minimum order for ${bill.delivery_charge_preview.delivery_area_name} is ${formatPlain(bill.delivery_charge_preview.min_order_value)} ${currencyCode}`,
          );
          return;
        }
      }
      const due = (() => {
        const base = Number(bill.net_amount ?? total);
        if (!roundOff) return base;
        return Number(formatPlain(base + calcRoundOff(base)));
      })();
      let tendered = due;
      if (method === "cash") {
        const raw = tenderedOverride !== undefined ? tenderedOverride : (buffer || "");
        if (raw !== "" && raw != null) {
          const v = parseFloat(raw);
          if (!isNaN(v) && v > 0) tendered = v;
        }
        if (tendered + 0.001 < due) {
          toast.error("Tendered amount is less than bill total");
          return;
        }
      }
      if (method === "split") {
        const raw = tenderedOverride !== undefined ? tenderedOverride : tenderedAmount;
        const cashAmt = parseFloat(raw);
        if (isNaN(cashAmt) || cashAmt < 0.01) {
          toast.error("Enter the cash amount");
          return;
        }
        if (cashAmt + 0.01 >= due) {
          processPayment("cash", String(cashAmt));
          return;
        }
        tendered = cashAmt;
      }
      paymentLockRef.current = true;
      prepareBillPrintWindow();
      paymentMutation.mutate({
        method,
        tendered,
        customer_name: cashReceiptName,
        customer_tax_number: cashReceiptTrn.trim() || null,
        round_off: roundOff,
        customerId: extra?.customerId,
      });
    },
    [bill, paymentMutation, paymentMethod, items.length, buffer, total, cashReceiptName, cashReceiptTrn, tenderedAmount, roundOff],
  );

  const openPayDialog = useCallback(
    (method = "cash", presetTendered = "") => {
      if (!bill || paymentMutation.isPending) return;
      if (bill.status !== "open" && bill.status !== "held") {
        toast.info("This bill is already paid.");
        return;
      }
      if (!billEditable) {
        toast.info(
          openBusinessDate
            ? `Only bills for business day ${openBusinessDate} can be edited.`
            : "Open a business day before editing bills.",
        );
        return;
      }
      if (!items.length) {
        toast.error("Add items before payment");
        return;
      }
      if (bill.order_type === "DL") {
        const hasAddress =
          bill.delivery_address ||
          (bill.delivery_building_no && bill.delivery_flat_no);
        if (!bill.delivery_contact || !hasAddress || !bill.delivery_area_id) {
          toast.error("Complete delivery details (phone, area, bldg+flat or landmark) before payment");
          setDeliveryDialogOpen(true);
          return;
        }
      }
      setPaymentMethod(method);
      setRoundOff(false);
      setTenderedAmount(presetTendered || formatPlain(bill.net_amount ?? total));
      setTenderedDirty(Boolean(presetTendered));
      setCashReceiptTrn(bill.customer_tax_number || "");
      setCashReceiptName(bill.customer_name || "Walk-in");
      setPayOpen(true);
    },
    [bill, paymentMutation.isPending, items.length, total, billEditable, openBusinessDate],
  );

  const appendTenderedDigit = useCallback((digit) => {
    setTenderedAmount((prev) => {
      const current = tenderedDirty ? String(prev ?? "") : "";
      if (digit === "." && current.includes(".")) return current || "0.";
      if (digit === "00") {
        if (!current || current === "0") return "0";
        return sanitizeTendered(current + "00", currencyDecimals);
      }
      if ((!current || current === "0") && digit !== ".") return digit;
      return sanitizeTendered(current + digit, currencyDecimals);
    });
    setTenderedDirty(true);
  }, [tenderedDirty, currencyDecimals]);

  const backspaceTendered = useCallback(() => {
    setTenderedDirty(true);
    setTenderedAmount((prev) => String(prev ?? "").slice(0, -1));
  }, []);

  const clearTendered = useCallback(() => {
    setTenderedDirty(true);
    setTenderedAmount("");
  }, []);

  const addComboItem = (combo, qty = 1) => {
    if (!bill || comboMutation.isPending) return;
    comboMutation.mutate({ menuItemId: combo.menu_item_id, qty });
    setRecentIds((prev) => [combo.menu_item_id, ...prev.filter((id) => id !== combo.menu_item_id)].slice(0, 12));
    setMode("QTY");
    setBuffer("");
  };

  const addItem = (m, size = null) => {
    if (!bill || lineMutation.isPending || comboMutation.isPending) return;
    if (bill.status !== "open" && bill.status !== "held") {
      toast.info("This bill is already paid. Start a new bill to sell.");
      return;
    }
    if (!billEditable) {
      toast.info(
        openBusinessDate
          ? `Only bills for business day ${openBusinessDate} can be edited.`
          : "Open a business day before editing bills.",
      );
      return;
    }
    const full = menuItems.find((i) => i.id === m.id) || m;
    if (comboItemIds.has(full.id)) {
      const combo = menuCombos.find((c) => c.menu_item_id === full.id);
      if (combo) return addComboItem(combo);
    }
    if (!size && itemHasSizes(full)) {
      setSizePickItem(full);
      return;
    }
    lineMutation.mutate({
      action: "add",
      payload: { menu_item_id: full.id, qty: 1, ...(size ? { size } : {}) },
      lineName: size ? `${full.name} (${size.charAt(0).toUpperCase()}${size.slice(1)})` : full.name,
    });
    setSizePickItem(null);
    setRecentIds((prev) => [full.id, ...prev.filter((id) => id !== full.id)].slice(0, 12));
    setMode("QTY");
    setBuffer("");
  };

  const addFastSlotItem = (slot) => {
    if (!slot?.menu_item_id) return;
    addItem({
      id: slot.menu_item_id,
      name: slot.name,
      code: slot.code,
      price: slot.price,
      unit: slot.unit,
      category: slot.category,
    });
    toast.success(`${slot.name} added (${slot.slot_key})`);
  };

  const adjustQty = (lineId, delta) => {
    if (lineMutation.isPending) return;
    const line = items.find((p) => p.id === lineId);
    if (!line) return;
    const actionLineId = billLineActionId(line);
    const actionLine = items.find((p) => p.id === actionLineId) || line;
    if (actionLine.qty + delta <= 0) {
      voidLine(actionLineId, actionLine.name);
      return;
    }
    lineMutation.mutate({
      action: "update",
      lineId: actionLineId,
      payload: { qty_delta: delta },
      lineName: actionLine.name,
    });
  };

  const voidLine = (lineId, lineName) => {
    if (lineMutation.isPending) return;
    lineMutation.mutate({ action: "void", lineId, lineName });
  };

  const removeItem = (lineId) => {
    const line = items.find((p) => p.id === lineId);
    voidLine(lineId, line?.name);
  };

  const clearAll = () => {
    startNewBill();
    toast.info("New bill started");
  };

  const setOrderType = (type) => {
    updateBillMutation.mutate(
      { order_type: type },
      {
        onSuccess: () => {
          if (type === "DL") {
            setDeliveryDialogOpen(true);
          } else {
            setDeliveryDialogOpen(false);
          }
        },
      },
    );
  };

  const posBackTarget = useMemo(() => {
    if (location.state?.openBillId || location.state?.openTable || location.state?.tableCode) {
      return { path: "/tables", label: t("pos.backToTables", "Tables") };
    }
    if (showBackOffice) {
      return { path: "/", label: t("pos.backOffice", "Back Office") };
    }
    return { path: "/tables", label: t("pos.back", "Back") };
  }, [location.state, showBackOffice, t]);

  const handlePosBack = useCallback(() => {
    navigate(posBackTarget.path);
  }, [navigate, posBackTarget.path]);

  const pushDigit = (k) => setBuffer((b) => (b + k).slice(0, 8));
  const backspace = () => setBuffer((b) => b.slice(0, -1));
  const clearBuffer = () => setBuffer("");
  const cancelMode = () => {
    setBuffer("");
    setMode("QTY");
    setKeypadOpen(false);
  };

  const applyBuffer = () => {
    if (!buffer) return toast.error("Enter a value first");
    const v = parseFloat(buffer);
    if (isNaN(v)) {
      setBuffer("");
      return toast.error("Invalid number");
    }
    switch (mode) {
      case "QTY": {
        if (!selectedLine) return toast.error("Select a bill line first");
        const q = Math.max(1, Math.floor(v));
        lineMutation.mutate({
          action: "update",
          lineId: selectedLine,
          payload: { qty: q },
          lineName: items.find((i) => i.id === selectedLine)?.name,
        });
        toast.success(`Qty set to ${q}`);
        break;
      }
      case "RATE": {
        if (!selectedLine) return toast.error("Select a bill line first");
        pricingMutation.mutate({ lineId: selectedLine, rate: v });
        break;
      }
      case "DISC": {
        const pct = Math.min(100, v);
        if (selectedLine) {
          discountMutation.mutate({ scope: "line", lineId: selectedLine, discount_percent: pct });
        } else {
          discountMutation.mutate({ scope: "bill", discount_percent: pct });
        }
        break;
      }
      case "PARCEL":
        chargeMutation.mutate({ type: "parcel", value: v });
        break;
      case "SVC":
        chargeMutation.mutate({ type: "service", value: Math.min(100, v) });
        break;
      case "TIP":
        if (!tipsOnPos) {
          toast.error("Tips are disabled on POS. Enable in Administration → General Settings.");
          break;
        }
        chargeMutation.mutate({ type: "tip", value: v });
        break;
      case "TABLE": {
        const t = `T-${String(Math.floor(v)).padStart(2, "0")}`;
        updateBillMutation.mutate({ table_code: t });
        toast.success(`Switched to ${t}`);
        break;
      }
      case "RECALL":
        recallBillMutation.mutate(buffer);
        break;
      case "CREDIT":
        break;
      default:
        break;
    }
    setBuffer("");
    setMode("QTY");
    setKeypadOpen(false);
  };

  const setOpMode = (m) => {
    setMode(m);
    setBuffer("");
    setKeypadOpen(true);
  };

  const openKeypad = useCallback(() => setKeypadOpen(true), []);

  function openKotReprint() {
    if (!bill?.id) {
      toast.error("Open a bill first");
      return;
    }
    setKotReprintOpen(true);
  }

  function openKitchenNotes() {
    const lineId = selectedLine ?? items[0]?.id ?? null;
    if (!lineId) {
      toast.error("Add an item to the bill first");
      return;
    }
    if (!selectedLine) setSelectedLine(lineId);
    const line = items.find((i) => i.id === lineId);
    setKnoteSelection((line?.kitchen_notes || []).map((n) => n.id));
    setKnoteNewName("");
    setKnoteDialogOpen(true);
    refetchKitchenNotes();
  }

  function toggleKnoteOption(id) {
    setKnoteSelection((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  }

  function saveKitchenNotes() {
    if (!selectedLine) return;
    const line = items.find((i) => i.id === selectedLine);
    const lineId = billLineActionId(line) || selectedLine;
    kitchenNoteMutation.mutate({
      lineId,
      kitchen_note_ids: knoteSelection,
    });
  }

  const loadCustomers = useCallback(async (search = "") => {
    if (!branchId) return;
    const fetchId = ++customerFetchRef.current;
    setCustomersLoading(true);
    try {
      const params = { branch_id: branchId };
      if (search) params.search = search;
      const { data } = await api.get("/customers", { params });
      if (fetchId !== customerFetchRef.current) return;
      setCustomerOptions(data.customers ?? []);
    } catch (err) {
      if (fetchId !== customerFetchRef.current) return;
      setCustomerOptions([]);
      toast.error(err.response?.data?.message || "Failed to load customers");
    } finally {
      if (fetchId === customerFetchRef.current) {
        setCustomersLoading(false);
      }
    }
  }, [branchId]);

  useEffect(() => {
    if (!customerDialogOpen && !creditDialogOpen) return undefined;
    const timer = setTimeout(() => {
      loadCustomers(customerSearch.trim());
    }, customerSearch ? 250 : 0);
    return () => clearTimeout(timer);
  }, [customerDialogOpen, creditDialogOpen, customerSearch, loadCustomers]);

  useEffect(() => {
    if (!billReprintOpen) return undefined;
    const timer = setTimeout(() => {
      loadReprintBills(reprintDateFrom, reprintDateTo, reprintSearch.trim());
    }, reprintSearch ? 250 : 0);
    return () => clearTimeout(timer);
  }, [billReprintOpen, reprintDateFrom, reprintDateTo, reprintSearch, loadReprintBills]);

  useEffect(() => {
    if (!creditDialogOpen || creditCustomer || !bill?.customer_id) return;
    const found = customerOptions.find((c) => c.id === bill.customer_id);
    if (found) setCreditCustomer(found);
  }, [creditDialogOpen, customerOptions, bill?.customer_id, creditCustomer]);

  useEffect(() => {
    if (!loyaltyOnPos || !bill?.customer_id || !branchId) {
      setLoyaltySummary(null);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/loyalty/customers/${bill.customer_id}/summary`, {
          params: { branch_id: branchId },
        });
        if (!cancelled) setLoyaltySummary(data);
      } catch {
        if (!cancelled) setLoyaltySummary(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [bill?.customer_id, branchId, loyaltyOnPos]);

  const redeemLoyaltyMutation = useMutation({
    mutationFn: async (points) => {
      const { data } = await api.post(`/bills/${bill.id}/loyalty/redeem`, { points }, {
        params: { branch_id: branchId },
      });
      return data;
    },
    onSuccess: (data) => {
      setBill(data.bill);
      setLoyaltyDialogOpen(false);
      setRedeemPoints("");
      toast.success(data.message);
    },
    onError: (err) => {
      toast.error(err.response?.data?.message || err.response?.data?.errors?.points?.[0] || "Redemption failed");
    },
  });

  const usualOrderMutation = useMutation({
    mutationFn: async () => {
      const { data } = await api.post(
        `/bills/${bill.id}/usual-order`,
        { customer_id: bill.customer_id, max_items: 3 },
        { params: { branch_id: branchId } },
      );
      return data;
    },
    onSuccess: (data) => {
      setBill(data.bill);
      setSelectedLine(data.bill.lines?.[0]?.id || null);
      toast.success(data.message);
    },
    onError: (err) => {
      toast.error(err.response?.data?.message || err.response?.data?.errors?.customer?.[0] || "No usual order found");
    },
  });

  function openCustomerDialog() {
    if (!bill?.id) {
      toast.error("Open a bill first");
      return;
    }
    setCustomerSearch("");
    setCustomerOptions([]);
    setCustomerDialogOpen(true);
  }

  function applyCustomer(customer) {
    if (!bill?.id) return;
    if (!customer) {
      updateBillMutation.mutate(
        { customer_id: null, customer_name: "Walk-in" },
        {
          onSuccess: () => {
            setCustomerDialogOpen(false);
            setCashReceiptName("Walk-in");
            toast.success("Customer set to Walk-in");
          },
        },
      );
      return;
    }
    updateBillMutation.mutate(
      { customer_id: customer.id, customer_name: customer.name },
      {
        onSuccess: () => {
          setCustomerDialogOpen(false);
          setCashReceiptName(customer.name);
          setCashReceiptTrn(customer.tax_number || "");
          toast.success(`Customer: ${customer.name}`);
        },
      },
    );
  }

  function openCreditSale() {
    if (!bill?.id) {
      toast.error("Open a bill first");
      return;
    }
    if (!items.length) {
      toast.error("Add items before credit sale");
      return;
    }
    setCustomerSearch("");
    setCustomerOptions([]);
    setCreditCustomer(null);
    setCreditDialogOpen(true);
  }

  async function createCreditCustomer(details) {
    const payload = {
      ...details,
      code: `CUST-${Date.now().toString(36).toUpperCase()}`,
    };
    const { data } = await api.post("/customers", payload, {
      params: { branch_id: branchId },
    });
    const created = data.customer;
    if (!created?.id) {
      throw new Error("Customer was not created");
    }
    setCreditCustomer(created);
    return created;
  }

  async function chargeCreditSale(customer) {
    try {
      if (bill.customer_id !== customer.id) {
        await updateBillMutation.mutateAsync({
          customer_id: customer.id,
          customer_name: customer.name,
        });
      }
      processPayment("credit", undefined, { customerId: customer.id });
    } catch (err) {
      toast.error(
        err.response?.data?.errors?.emirates_id_expires_on?.[0]
          || err.response?.data?.errors?.customer_id?.[0]
          || err.response?.data?.message
          || "Failed to charge credit sale",
      );
    }
  }

  blockPosKeysRef.current = (
    returnDialogOpen
    || knoteDialogOpen
    || customerDialogOpen
    || creditDialogOpen
    || loyaltyDialogOpen
    || fastMenuOpen
    || comboMenuOpen
    || kotReprintOpen
    || billReprintOpen
    || deliveryDialogOpen
    || closeShiftOpen
    || payOpen
  );

  useEffect(() => {
    const onKeyDown = (e) => {
      if (blockPosKeysRef.current) {
        return;
      }

      if (e.key === "o" && e.ctrlKey) {
        e.preventDefault();
        if (!drawerMutation.isPending) drawerMutation.mutate();
        return;
      }
      if (e.key === "c" && e.ctrlKey) {
        e.preventDefault();
        setComboMenuOpen((v) => !v);
        return;
      }
      if (e.ctrlKey || e.altKey || e.metaKey) return;

      if (e.key === "Escape") {
        if (keypadOpen) {
          e.preventDefault();
          cancelMode();
        }
        return;
      }
      const typingInField = isPosTextEntryTarget(e.target);

      if (e.key === "Enter") {
        if (typingInField) return;
        if (buffer || mode !== "QTY") {
          e.preventDefault();
          applyBuffer();
        }
        return;
      }
      if (e.key === "Backspace") {
        if (typingInField) return;
        if (keypadOpen || buffer) {
          e.preventDefault();
          backspace();
        }
        return;
      }
      if (/^[0-9.]$/.test(e.key)) {
        if (typingInField) return;
        e.preventDefault();
        setKeypadOpen(true);
        pushDigit(e.key);
        return;
      }
      if (mode === "RECALL" && /^[a-zA-Z\-]$/.test(e.key)) {
        if (typingInField) return;
        e.preventDefault();
        setKeypadOpen(true);
        setBuffer((b) => (b + e.key.toUpperCase()).slice(0, 20));
        return;
      }
      if (e.key === "F3") {
        e.preventDefault();
        if (selectedLine) {
          const line = items.find((i) => i.id === selectedLine);
          voidLine(selectedLine, line?.name);
        } else {
          toast.error("Select a line to void");
        }
        return;
      }
      if (e.key === "F4") {
        e.preventDefault();
        setOpMode("DISC");
        return;
      }
      if (e.key === "F2") {
        e.preventDefault();
        setOpMode("RECALL");
        return;
      }
      if (e.key === "F5") {
        e.preventDefault();
        setOrderType("DI");
        return;
      }
      if (e.key === "F7") {
        e.preventDefault();
        setOrderType("DL");
        return;
      }
      if (e.key === "/") {
        e.preventDefault();
        searchInputRef.current?.focus();
        return;
      }

      if (e.key === "F6") {
        e.preventDefault();
        openKotReprint();
        return;
      }
      if (e.key === "F8") {
        e.preventDefault();
        openKitchenNotes();
        return;
      }
      if (e.key === "F10") {
        e.preventDefault();
        openBillReprint();
        return;
      }
      if (e.key === "F11") {
        e.preventDefault();
        setReturnDialogOpen(true);
        return;
      }
      if (e.key === "F12") {
        e.preventDefault();
        openCreditSale();
        return;
      }

      if (fastMenuEnabled && fastSlotMap[e.key]) {
        e.preventDefault();
        addFastSlotItem(fastSlotMap[e.key]);
        return;
      }

      if (e.key === "F1") {
        e.preventDefault();
        if (!bill || holdBillMutation.isPending) return;
        holdBillMutation.mutate();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    selectedLine,
    items,
    bill,
    holdBillMutation.isPending,
    fastMenuEnabled,
    fastSlotMap,
    buffer,
    mode,
    keypadOpen,
  ]);

  const onActionClick = (a) => {
    if (a.mode) return setOpMode(a.mode);
    if (a.id === "clear") return clearAll();
    if (a.id === "hold") {
      if (!bill || holdBillMutation.isPending) return;
      return holdBillMutation.mutate();
    }
    if (a.id === "kotsend") {
      if (!bill || !items.length) return toast.error("Add items before sending KOT");
      if (dineInOrder(bill) && !billHasAssignedTable(bill)) {
        toast.error(t("pos.tableRequiredForKot", "Select a table before sending KOT"));
        setOpMode("TABLE");
        return;
      }
      if (sendKotMutation.isPending) return;
      return sendKotMutation.mutate();
    }
    if (a.id === "kotreprint") return openKotReprint();
    if (a.id === "knote") return openKitchenNotes();
    if (a.id === "credit") return openCreditSale();
    if (a.id === "fast") return setFastMenuOpen((v) => !v);
    if (a.id === "combo") return setComboMenuOpen((v) => !v);
    if (a.id === "mark") return toast.info("Group marked");
    if (a.id === "drawer") {
      if (drawerMutation.isPending) return;
      return drawerMutation.mutate();
    }
    if (a.id === "void") {
      if (selectedLine) {
        const line = items.find((i) => i.id === selectedLine);
        voidLine(selectedLine, line?.name);
      } else toast.error("Select a line to void");
      return;
    }
    if (a.id === "return") return setReturnDialogOpen(true);
    if (a.id === "closeshift") return openCloseShift();
    if (a.id === "splitbill" || a.id === "mergetable" || a.id === "transfertable" || a.id === "pendingorders") {
      return navigate("/tables", { state: { fromPos: true, billId: bill?.id, tableCode: bill?.table_code } });
    }
    if (a.id === "openbills") return navigate("/pos/held-bills");
    if (a.id === "kothistory") return openKotReprint();
    if (a.id === "changewaiter") {
      return navigate("/tables", { state: { fromPos: true, billId: bill?.id } });
    }
  };

  const orderTypes = useMemo(
    () => ORDER_TYPE_DEFS.map((o) => ({ ...o, label: t(o.labelKey) })),
    [t],
  );

  const actions = useMemo(
    () => ACTION_DEFS.map((a) => ({
      ...a,
      label: a.labelKey ? t(a.labelKey) : a.label,
    })),
    [t],
  );

  const gridActions = useMemo(
    () => GRID_ACTION_IDS.map((id) => actions.find((a) => a.id === id)).filter(Boolean),
    [actions],
  );

  const renderActionButton = (a) => {
    const isActive = a.mode && a.mode === mode;
    const style = POS_ACTION_STYLES[a.id] || POS_ACTION_STYLES.credit;
    const Icon = ACTION_ICONS[a.id];
    return (
      <button
        key={a.id}
        type="button"
        data-testid={`action-${a.id}`}
        onClick={() => onActionClick(a)}
        className={cn(
          "pos-action-btn border font-bold flex flex-col items-center justify-center transition-all touch-manipulation",
          "text-[8.5px] gap-0 px-0.5",
          isActive && "ring-2 ring-brand-orange ring-offset-1",
          "active:scale-[0.97]",
        )}
        style={{
          background: style.bg,
          borderColor: style.border,
          color: style.text,
        }}
      >
        {Icon && <Icon size={13} strokeWidth={2.25} className="shrink-0" />}
        <span className="leading-tight text-center line-clamp-2">{a.label}</span>
        {a.key && (
          <span className="text-[6.5px] font-mono opacity-55 leading-none">{a.key}</span>
        )}
      </button>
    );
  };

  const resolveModeConfig = useCallback((modeKey) => {
    const def = MODE_CONFIG_DEFS[modeKey] || MODE_CONFIG_DEFS.QTY;
    const suffix = def.suffixKey === "currency" ? currencyCode : (def.suffix ?? "");
    return {
      ...def,
      suffix,
      label: t(def.labelKey),
      hint: t(def.hintKey),
    };
  }, [t, currencyCode]);

  const currentMode = useMemo(() => {
    if (mode === "DISC" && selectedLine) {
      const line = items.find((i) => i.id === selectedLine);
      return {
        ...resolveModeConfig("DISC"),
        label: t("pos.mode.discItem.label"),
        hint: t("pos.mode.discItem.hint", { name: line?.name || "—" }),
      };
    }
    if (mode === "DISC") {
      return {
        ...resolveModeConfig("DISC"),
        label: t("pos.mode.discBill.label"),
        hint: t("pos.mode.discBill.hint", { max: bill?.max_discount_percent ?? 25 }),
      };
    }
    if (mode === "RATE" && selectedLine) {
      const line = items.find((i) => i.id === selectedLine);
      return {
        ...resolveModeConfig("RATE"),
        hint: t("pos.mode.rateLine.hint", {
          name: line?.name || "—",
          rate: formatPlain((line?.original_rate ?? line?.rate) || 0),
        }),
      };
    }
    return resolveModeConfig(mode);
  }, [mode, selectedLine, items, bill?.max_discount_percent, resolveModeConfig, t, formatPlain]);
  const activeModeId = mode;

  if (!branchId || !counterId) {
    return (
      <div className="h-full grid place-items-center text-slate-600 text-sm px-4 text-center">
        <p>{t("pos.selectBranchCounter")}</p>
        <p className="text-[12px] text-slate-400 mt-2 max-w-sm">{t("counter.terminalHint")}</p>
      </div>
    );
  }

  if (businessDayLoading) {
    return (
      <div className="h-full grid place-items-center text-slate-600 text-sm gap-2">
        <Loader2 className="h-5 w-5 animate-spin text-slate-400" />
        Checking business day…
      </div>
    );
  }

  if (!dayIsOpen) {
    return (
      <DayOpenGate
        businessDay={businessDay}
        busy={openDayMutation.isPending || businessDayFetching}
        onOpenDay={async (businessDate, twoFactorCode) => {
          await openDayMutation.mutateAsync({ businessDate, twoFactorCode });
        }}
        onLeave={() => navigate(showBackOffice ? "/" : "/login")}
      />
    );
  }

  if (shiftLoading) {
    return (
      <div className="h-full grid place-items-center text-slate-600 text-sm gap-2">
        <Loader2 className="h-5 w-5 animate-spin text-slate-400" />
        {t("common.loading")}
      </div>
    );
  }

  if (!shiftOpen) {
    return (
      <div className="h-full grid place-items-center bg-brand-cream p-4" data-testid="pos-shift-gate">
        <div className="w-full max-w-md rounded-xl border border-brand-cream-border bg-white p-6 shadow-md">
          <h2 className="text-[17px] font-bold text-brand-brown">{t("pos.shiftRequiredTitle")}</h2>
          <p className="text-[13px] text-slate-600 mt-2">
            {t("pos.shiftRequiredBody", { counter: counterName })}
          </p>
          <div className="mt-4 space-y-1.5">
            <Label htmlFor="pos-shift-float">{t("counter.openingFloat")}</Label>
            <Input
              id="pos-shift-float"
              type="number"
              min="0"
              step={moneyStep}
              value={shiftFloat}
              onChange={(e) => setShiftFloat(e.target.value)}
              data-testid="pos-shift-float"
            />
            <p className="text-[11px] text-slate-500">{t("counter.openShiftHelp")}</p>
          </div>
          <Button
            className="w-full mt-4 bg-brand-green hover:bg-brand-green/90"
            disabled={openShiftMutation.isPending}
            data-testid="pos-open-shift"
            onClick={() => openShiftMutation.mutate()}
          >
            {openShiftMutation.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              t("pos.openShift")
            )}
          </Button>
        </div>
      </div>
    );
  }

  if (bootstrapping || !bill) {
    return (
      <div className="h-full grid place-items-center text-slate-600 text-sm gap-3 px-4" data-testid="pos-loading">
        {bootError ? (
          <>
            <p className="text-red-600 text-center max-w-sm">{bootError}</p>
            <Button
              size="sm"
              onClick={() => {
                setBootError(null);
                setBootstrapping(true);
                runPosBootstrap(`retry:${branchId}:${counterId}:${Date.now()}`, async () => {
                  const { data } = await api.post(
                    "/bills",
                    { order_type: "DI" },
                    { params: { branch_id: branchId, counter_id: counterId } },
                  );
                  return { bill: data.bill };
                })
                  .then((result) => {
                    if (result?.bill) applyLoadedBill(result.bill);
                  })
                  .catch((err) => {
                    const msg = err.response?.data?.message || "Failed to open POS";
                    setBootError(msg);
                    toast.error(msg);
                  })
                  .finally(() => setBootstrapping(false));
              }}
            >
              {t("common.retry")}
            </Button>
          </>
        ) : (
          <>
            <Loader2 className="h-5 w-5 animate-spin text-slate-400" />
            {t("pos.opening")}
          </>
        )}
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col bg-[#FAF9F6] overflow-hidden" dir={isRtl ? "rtl" : "ltr"} data-testid="pos-screen">
      {/* ── TOP STRIP — warm light header ── */}
      <div className="bg-white px-2.5 py-1 flex items-center gap-1.5 flex-nowrap overflow-x-auto text-[12px] flex-shrink-0 min-h-[40px] border-b border-[#F0E0D4] shadow-sm">
        <button
          type="button"
          onClick={handlePosBack}
          className="h-9 min-h-[36px] px-2.5 rounded-lg border border-[#F0E0D4] bg-[#FFFCFA] hover:bg-[#FFF3E8] flex items-center gap-1.5 shrink-0 touch-manipulation text-brand-brown font-bold text-[11px]"
          title={posBackTarget.label}
          data-testid="pos-back-btn"
        >
          <ArrowLeft size={14} className="text-brand-orange shrink-0" />
          <span className="hidden sm:inline">{posBackTarget.label}</span>
        </button>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              className="h-9 w-9 min-h-[36px] min-w-[36px] rounded-lg bg-brand-orange flex items-center justify-center shrink-0 hover:bg-brand-orange-light shadow-sm touch-manipulation"
              title="POS menu"
              data-testid="pos-menu-btn"
            >
              <Menu size={15} className="text-white" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="min-w-[240px] max-h-[min(70vh,420px)]">
            <DropdownMenuLabel className="text-[10px] uppercase tracking-wider text-slate-500">
              {t("pos.heldBills", "Held bills")}
            </DropdownMenuLabel>
            {heldBills.length === 0 ? (
              <div className="px-2 py-2 text-[12px] text-slate-500">
                {t("pos.noHeldBills", "No held bills")}
              </div>
            ) : (
              heldBills.map((held) => (
                  <DropdownMenuItem
                    key={held.id}
                    disabled={openHeldBillMutation.isPending}
                    onClick={() => openHeldBillMutation.mutate(held)}
                    className="flex items-center justify-between gap-3"
                    data-testid={`pos-menu-held-${held.id}`}
                  >
                    <span className="font-bold text-[13px]">{heldWhereLabel(held, t)}</span>
                    <span className="font-mono text-[12px] text-slate-600">{heldBillNo(held)}</span>
                  </DropdownMenuItem>
                ))
            )}
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => openBillReprint()}>Reprint bill</DropdownMenuItem>
            <DropdownMenuItem onClick={() => navigate("/reports/bill-history")}>POS bill history</DropdownMenuItem>
            <DropdownMenuItem onClick={() => navigate("/tables")}>Table Floor</DropdownMenuItem>
            <DropdownMenuItem onClick={() => navigate("/tables")}>Pending Orders</DropdownMenuItem>
            <DropdownMenuItem onClick={() => openCloseShift()}>Close shift</DropdownMenuItem>
            {showBackOffice && (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate("/")}>Back Office</DropdownMenuItem>
              </>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
        <button
          type="button"
          onClick={() => openBillReprint()}
          className="h-9 min-h-[36px] px-2.5 rounded-lg border border-[#F0E0D4] bg-[#FFFCFA] hover:bg-[#FFF3E8] flex items-center gap-1.5 shrink-0 touch-manipulation text-brand-brown font-bold text-[11px]"
          title="Reprint bill history"
          data-testid="pos-history-btn"
        >
          <History size={14} className="text-brand-orange shrink-0" />
          <span className="hidden sm:inline">{t("pos.billHistory", "History")}</span>
        </button>
        {offlinePending > 0 ? (
          <button
            type="button"
            className="px-2.5 h-9 min-h-[36px] rounded-full bg-amber-400 text-amber-950 font-semibold shrink-0 text-[11px] touch-manipulation"
            onClick={() => syncQueue()}
            title="Sync queued offline payments"
          >
            Offline: {offlinePending}
          </button>
        ) : null}
        <div className="flex items-center gap-1 shrink-0">
          <button
            type="button"
            data-testid="pos-bill-prev"
            title="Previous bill"
            disabled={!sessionNav?.has_prev || sessionNavMutation.isPending}
            onClick={goSessionPrev}
            className="h-9 w-9 min-h-[36px] min-w-[36px] rounded-lg bg-white border border-[#F0E0D4] shadow-sm grid place-items-center text-brand-brown hover:bg-[#FFF3E8] touch-manipulation disabled:opacity-40"
          >
            <ChevronLeft size={16} />
          </button>
          <div className="flex items-center gap-1.5 px-2.5 h-9 min-h-[36px] bg-white rounded-full shadow-sm shrink-0">
            <Receipt size={13} className="text-violet-500 shrink-0" />
            <span className="text-brand-brown/50 font-semibold text-[10px] uppercase">{t("pos.orderNumber")}</span>
            <span className="font-mono font-bold text-brand-brown text-[12px]">{bill.bill_number}</span>
            {(() => {
              const ot = ORDER_TYPE_DEFS.find((o) => o.id === orderType) || ORDER_TYPE_DEFS[0];
              const OtIcon = ot.icon;
              const otStyle = ORDER_TYPE_STYLES[orderType] || ORDER_TYPE_STYLES.DI;
              return (
                <span
                  className={cn(
                    "inline-flex items-center gap-1 text-[9px] font-black uppercase tracking-wide px-1.5 py-0.5 rounded-full",
                    otStyle.active,
                  )}
                  data-testid="pos-bill-order-type"
                  title={t(ot.labelKey)}
                >
                  <OtIcon size={10} />
                  {t(ot.labelKey)}
                </span>
              );
            })()}
            {(bill.status === "paid" || bill.status === "partial_return") && (
              <span className="text-[9px] font-black uppercase tracking-wide text-emerald-700 bg-emerald-50 px-1.5 py-0.5 rounded-full">
                Paid
              </span>
            )}
            {billViewOnly && bill.status !== "paid" && bill.status !== "partial_return" && (
              <span className="text-[9px] font-black uppercase tracking-wide text-slate-600 bg-slate-100 px-1.5 py-0.5 rounded-full">
                View only
              </span>
            )}
          </div>
          <button
            type="button"
            data-testid="pos-bill-next"
            title={sessionNav?.has_next ? "Next bill" : "New bill"}
            disabled={!canGoNext || sessionNavMutation.isPending || createBillMutation.isPending}
            onClick={goSessionNext}
            className="h-9 w-9 min-h-[36px] min-w-[36px] rounded-lg bg-white border border-[#F0E0D4] shadow-sm grid place-items-center text-brand-brown hover:bg-[#FFF3E8] touch-manipulation disabled:opacity-40"
          >
            <ChevronRight size={16} />
          </button>
        </div>
        <div className="flex items-center gap-1.5 px-2.5 h-9 min-h-[36px] bg-white rounded-full shadow-sm shrink-0">
          <User size={11} className="text-brand-orange" />
          <span className="text-brand-brown/60 font-semibold text-[11px]">{t("pos.customer")}</span>
          <span className="font-bold text-brand-brown text-[11px]">{bill.customer_name}</span>
          <button
            type="button"
            className="text-[10px] text-brand-orange font-bold ms-1 underline disabled:opacity-40 disabled:no-underline"
            data-testid="change-customer"
            disabled={!billEditable}
            onClick={openCustomerDialog}
          >
            {t("pos.change")}
          </button>
        </div>
        {loyaltyOnPos && bill?.customer_id && loyaltySummary?.settings?.is_enabled && (
          <div className="flex items-center gap-1 px-2 h-7 bg-amber-50 rounded-sm border border-amber-300">
            <Gift size={11} className="text-amber-700" />
            <span className="text-[10.5px] font-bold text-amber-900">
              {loyaltySummary.balance} pts
            </span>
            <button
              type="button"
              className="text-[10px] text-amber-800 underline font-semibold"
              onClick={() => {
                setRedeemPoints(String(loyaltySummary.balance || ""));
                setLoyaltyDialogOpen(true);
              }}
              data-testid="pos-redeem-loyalty"
            >
              Redeem
            </button>
            <button
              type="button"
              className="text-[10px] text-amber-800 underline font-semibold flex items-center gap-0.5"
              onClick={() => usualOrderMutation.mutate()}
              disabled={usualOrderMutation.isPending}
              data-testid="pos-usual-order"
            >
              <History size={10} />
              Usual
            </button>
          </div>
        )}
        <button
          data-testid="table-search"
          onClick={() => setOpMode("TABLE")}
          className={cn(
            "flex items-center gap-1.5 px-2.5 h-9 min-h-[36px] rounded-full shadow-sm shrink-0 touch-manipulation",
            activeModeId === "TABLE"
              ? "bg-brand-orange text-white ring-2 ring-brand-orange/25"
              : "bg-[#FFFCFA] border border-[#F0E0D4] hover:bg-[#FFF3E8]",
          )}
        >
          <span className={cn("font-semibold text-[11px]", activeModeId === "TABLE" ? "text-white/80" : "text-brand-brown/60")}>{t("pos.table")}</span>
          <span className={cn("font-bold text-[11px]", activeModeId === "TABLE" ? "text-white" : "text-brand-brown")}>{tableNo}</span>
          <Search size={11} className="text-brand-orange ml-0.5" />
        </button>
        <div className="flex items-center gap-1 h-9 rounded-full bg-[#FFFCFA] border border-[#F0E0D4] p-0.5 shadow-sm shrink-0">
          {orderTypes.map((o) => {
            const Icon = o.icon;
            const otStyle = ORDER_TYPE_STYLES[o.id] || ORDER_TYPE_STYLES.DI;
            const isActive = orderType === o.id;
            return (
              <button
                key={o.id}
                data-testid={`order-type-${o.id}`}
                onClick={() => setOrderType(o.id)}
                className={cn(
                  "h-8 min-h-[32px] min-w-[36px] px-2.5 rounded-full text-[10px] font-bold flex items-center gap-1 touch-manipulation",
                  isActive ? otStyle.active : otStyle.idle,
                )}
              >
                <Icon size={12} /> {o.label.toUpperCase()}
                <span
                  className={cn(
                    "text-[8px] font-mono px-1 rounded",
                    isActive ? otStyle.key : "bg-white/80 text-inherit opacity-70",
                  )}
                >
                  {o.key}
                </span>
              </button>
            );
          })}
        </div>
        <div className="ml-auto flex items-center gap-1.5 shrink-0">
          <ClockWeatherWidget variant="inline" className="hidden md:flex" />
          <button
            data-testid="pos-new-order"
            onClick={() => startNewBill()}
            className="h-9 px-3.5 bg-brand-orange rounded-lg text-[11px] font-bold text-white hover:bg-brand-orange-light shadow-md shrink-0 touch-manipulation min-h-[36px]"
          >
            + NEW BILL
          </button>
        </div>
      </div>

      {heldBills.length > 0 ? (
        <div
          className="flex-shrink-0 flex items-center gap-1.5 px-2.5 py-1 border-b border-amber-200/80 bg-amber-50/80 overflow-x-auto"
          data-testid="pos-held-bills"
        >
          <span className="flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-amber-800 shrink-0">
            <Pause size={11} />
            {t("pos.heldBills", "Held")}
          </span>
          {heldBills.map((held) => (
            <button
              key={held.id}
              type="button"
              data-testid={`pos-held-${held.id}`}
              disabled={openHeldBillMutation.isPending}
              onClick={() => openHeldBillMutation.mutate(held)}
              className="h-10 min-h-[40px] px-2.5 rounded-lg bg-white border border-amber-300 text-amber-950 shrink-0 hover:bg-amber-100 touch-manipulation disabled:opacity-50 text-left leading-tight"
              title={t("pos.openHeldBill", "Open this bill")}
            >
              <span className="block font-black text-[12px]">{heldWhereLabel(held, t)}</span>
              <span className="block font-mono text-[10px] font-bold text-amber-800">{heldBillNo(held)}</span>
            </button>
          ))}
        </div>
      ) : null}

      {/* ── MAIN GRID ── */}
      <div className="flex-1 flex min-h-0 gap-1.5 p-1.5">
        {/* LEFT — Categories */}
        <aside
          className={cn(
            "flex-shrink-0 bg-white rounded-xl flex flex-col shadow-sm border border-[#F0E0D4]/80 transition-[width] duration-200",
            catCollapsed ? "w-[64px]" : "w-[200px] xl:w-[220px]",
          )}
          data-testid="pos-category-panel"
        >
          <div className="h-9 px-2 flex items-center justify-between text-[10px] font-bold text-brand-brown uppercase tracking-widest flex-shrink-0 border-b border-[#F5EBE3]">
            {!catCollapsed && <span>Categories</span>}
            <button
              type="button"
              onClick={() => setCatCollapsed((v) => !v)}
              className="h-8 w-8 min-h-[32px] min-w-[32px] rounded-lg hover:bg-[#FFF3E8] grid place-items-center text-brand-orange shrink-0 touch-manipulation"
              title={catCollapsed ? "Expand categories" : "Collapse categories"}
              data-testid="pos-cat-collapse"
            >
              {catCollapsed ? <ChevronRight size={14} /> : <ChevronLeft size={14} />}
            </button>
          </div>
          <div className="flex-1 overflow-y-auto p-2 space-y-1.5 min-h-0">
            {categories.map((c) => {
              const active = view === "category" && activeCategory === c.name;
              const catImg = resolveMediaUrl(categoryImages[c.name]);
              return (
                <button
                  key={c.id}
                  data-testid={`cat-${c.id}`}
                  title={catCollapsed ? c.name : undefined}
                  onClick={() => { setActiveCategory(c.name); setView("category"); }}
                  className={cn(
                    "w-full rounded-xl font-semibold border transition-all touch-manipulation active:scale-[0.98]",
                    catCollapsed
                      ? "h-12 flex flex-col items-center justify-center gap-0.5 px-1"
                      : "h-[48px] flex items-center gap-2 px-2 text-[11px]",
                    active
                      ? "pos-category-active text-white border-transparent shadow-md"
                      : "bg-white border-[#F0E0D4] text-brand-brown shadow-sm hover:border-brand-orange/35 hover:shadow-md",
                  )}
                >
                  <span className={cn("leading-none shrink-0 flex items-center justify-center overflow-hidden rounded-md", catCollapsed ? "h-7 w-7" : "h-8 w-8")}>
                    {catImg ? (
                      <img src={catImg} alt="" className="w-full h-full object-cover rounded-md" />
                    ) : (
                      <span className={catCollapsed ? "text-[22px]" : "text-[18px]"}>{getCatIcon(c.name)}</span>
                    )}
                  </span>
                  {!catCollapsed && (
                    <>
                      <span className="flex-1 text-left leading-snug whitespace-normal break-words">
                        {c.name}
                      </span>
                      <span className={cn(
                        "text-[10px] font-bold min-w-[22px] h-6 px-1.5 rounded-full flex items-center justify-center shrink-0",
                        active ? "bg-white/25 text-white" : "bg-[#FFF3E8] text-brand-orange",
                      )}>
                        {c.count}
                      </span>
                    </>
                  )}
                  {catCollapsed && (
                    <span className={cn(
                      "text-[9px] font-bold rounded-full px-1 min-w-[18px] text-center",
                      active ? "text-white/90" : "text-brand-orange",
                    )}>
                      {c.count}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </aside>

        {/* CENTER — fixed zones: order scroll | summary | products */}
        <section className="pos-center-panel flex-1 flex flex-col min-w-0 min-h-0 bg-white rounded-xl shadow-sm border border-[#F0E0D4]/80 overflow-hidden">
          {orderType === "DL" && bill && (
            <DeliveryPanel
              variant="compact"
              bill={bill}
              branchId={branchId}
              onOpenDetails={() => setDeliveryDialogOpen(true)}
            />
          )}

          {/* 1. ORDER LIST — fixed flex slot; internal scroll only */}
          <div className="pos-order-zone bg-white" data-testid="pos-order-zone">
            <div className="flex-shrink-0 px-2 py-1.5 border-b border-[#F5EBE3] bg-white">
              <div className="flex items-center bg-[#FFFCFA] border border-[#F0E0D4] rounded-lg h-8 px-2.5 shadow-sm">
                <Search size={13} className="text-brand-orange mr-1.5 shrink-0" />
                <Input
                  ref={searchInputRef}
                  data-testid="pos-search"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search item / code / barcode…"
                  className="border-0 shadow-none focus-visible:ring-0 h-7 px-0 bg-transparent text-[12px] text-brand-brown placeholder:text-brand-brown/40"
                  autoComplete="off"
                />
                {search ? (
                  <button
                    type="button"
                    onClick={() => setSearch("")}
                    className="h-6 w-6 rounded-md hover:bg-[#FFF3E8] grid place-items-center text-brand-brown/50 shrink-0 touch-manipulation"
                    title="Clear search"
                    data-testid="pos-search-clear"
                  >
                    <X size={12} />
                  </button>
                ) : (
                  <span className="text-[9px] font-mono text-brand-brown/30 shrink-0 ms-1">/</span>
                )}
              </div>
            </div>
            <div className="h-7 px-2 grid grid-cols-[28px_1fr_44px_64px_76px_22px] gap-1.5 items-center text-[9px] font-bold text-[#A67C6A] uppercase tracking-wider flex-shrink-0 bg-[#FFFCFA] border-b border-[#F5EBE3]">
              <span>{t("pos.colSr")}</span><span>{t("pos.colItem")}</span>
              <span className="text-end">{t("pos.colQty")}</span><span className="text-end">{t("pos.colRate")}</span>
              <span className="text-end">{t("pos.colAmount")}</span><span></span>
            </div>
            <div
              className="pos-order-scroll pos-dot-grid"
              dir={isRtl ? "rtl" : "ltr"}
              data-testid="bill-grid"
            >
              {items.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-4 text-center px-6">
                  <PosEmptyIllustration className="w-16 h-14 mb-2 opacity-90" />
                  <p className="text-[11px] text-brand-brown/45 font-medium max-w-sm leading-relaxed">
                    {t("pos.billEmpty")}
                  </p>
                </div>
              ) : (
                billDisplayRows.map(({ line: it, serial, isComboChild, showComboGap }) => {
                  const selectId = billLineActionId(it);
                  const sel = selectedLine === selectId || selectedLine === it.id;
                  return (
                    <div
                      key={it.id}
                      onClick={() => { setSelectedLine(selectId); setMode("QTY"); setBuffer(""); }}
                      data-testid={`bill-line-${it.id}`}
                      className={cn(
                        "px-2 grid grid-cols-[28px_1fr_44px_64px_76px_22px] gap-1.5 items-center border-b cursor-pointer text-[12px]",
                        isComboChild && !sel && "bg-slate-50/80 text-[11.5px] pl-4",
                        showComboGap && "border-t-2 border-t-slate-300",
                        sel
                          ? "bg-brand-cream-panel border-l-[5px] border-l-brand-orange border-b-brand-cream-border min-h-[36px] font-bold"
                          : "border-brand-cream-border hover:bg-brand-cream/60 min-h-[34px]",
                      )}
                      style={sel ? { paddingLeft: isComboChild ? 12 : 2 } : {}}
                    >
                      <span className={cn("font-mono", sel ? "text-amber-900" : "text-slate-500")}>
                        {sel && <ChevronRight size={11} className="inline mr-0.5" />}
                        {serial ?? ""}
                      </span>
                      <span className={cn("truncate", sel ? "text-slate-900 font-extrabold" : "font-semibold text-slate-900")}>
                        {displayItemName(it)}
                        {itemNameSecondary(it) ? (
                          <span className="block text-[9.5px] font-semibold text-slate-500 truncate" dir={itemNameSecondary(it).match(/[\u0600-\u06FF]/) ? "rtl" : undefined}>
                            {itemNameSecondary(it)}
                          </span>
                        ) : null}
                        {it.kitchen_note_labels?.length > 0 && (
                          <span className="block text-[9.5px] font-semibold text-emerald-700 truncate">
                            {it.kitchen_note_labels.join(" · ")}
                          </span>
                        )}
                      </span>
                      <span className={cn("text-right font-mono font-bold text-[12.5px]", sel && "text-amber-900")}>
                        {qty2(it.qty)}
                      </span>
                      <span className={cn("text-right font-mono text-[12px]", sel && "text-amber-900 font-bold", it.is_rate_overridden && "text-violet-700")}>
                        {money2(it.rate)}
                        {it.is_rate_overridden && <span className="text-[9px] ml-0.5">*</span>}
                      </span>
                      <span className={cn("text-right font-mono font-bold text-[12.5px]", sel ? "text-amber-900" : "text-slate-900")}>
                        {money2(it.amount)}
                      </span>
                      <button
                        onClick={(e) => { e.stopPropagation(); removeItem(it.id); }}
                        data-testid={`remove-${it.id}`}
                        className={cn("grid place-items-center", sel ? "text-amber-800 hover:text-red-700" : "text-slate-400 hover:text-red-600")}
                      >
                        <X size={13} />
                      </button>
                    </div>
                  );
                })
              )}
            </div>
          </div>

          {/* 3+4. PRODUCT TABS + GRID — fixed zone; grid scrolls internally */}
          <div className="pos-products-zone bg-[#FAF9F6] border-t border-[#F5EBE3]" data-testid="pos-products-zone">
            <div className="flex items-center bg-white border-b border-[#F5EBE3] flex-shrink-0 gap-1 min-h-[32px]">
              <ViewTab active={view === "category"} onClick={() => setView("category")} icon={<Grid3x3 size={11} />} label={(activeCategory || "MENU").toUpperCase()} count={view === "category" ? visible.length : null} testid="ptab-category" compact />
              <ViewTab active={view === "recent"} onClick={() => setView("recent")} icon={<Clock size={11} />} label={t("common.recent").toUpperCase()} count={recentItems.length} testid="ptab-recent" compact />
              <ViewTab active={view === "top"} onClick={() => setView("top")} icon={<Flame size={11} />} label={t("common.topToday").toUpperCase()} count={topToday.length} testid="ptab-top" compact />
              <span className="ms-auto px-2 text-[10px] font-bold text-brand-brown/40 uppercase tracking-wider whitespace-nowrap">
                {visible.length} {t("waiter.items")}
              </span>
            </div>
            <div
              className="pos-products-scroll"
              data-testid="product-grid"
            >
              <div className="pos-product-grid">
              {menuLoading && visible.length === 0 ? (
                <div className="col-span-full flex items-center justify-center gap-2 py-6 text-brand-brown/50 text-[12px]">
                  <Loader2 className="h-4 w-4 animate-spin text-brand-orange" />
                  {t("common.loadingMenu")}
                </div>
              ) : null}
              {visible.map((m) => (
                <PosProductButton
                  key={m.id}
                  item={m}
                  disabled={lineMutation.isPending}
                  onAdd={addItem}
                />
              ))}
              {visible.length === 0 && (
                <div className="col-span-full text-center text-[11.5px] text-brand-brown/40 py-6">{t("common.noItems")}</div>
              )}
              </div>
            </div>
          </div>
        </section>

        {/* RIGHT — Operations (fixed 4×4) + Payment */}
        <aside className="w-[300px] flex-shrink-0 bg-white rounded-xl flex flex-col min-h-0 overflow-hidden shadow-sm border border-[#F0E0D4]/80">
          <div className="h-8 px-3 flex items-center text-[10px] font-bold text-brand-brown uppercase tracking-widest flex-shrink-0 border-b border-[#F5EBE3] bg-[#FFFCFA]">
            {t("pos.operations")}
          </div>
          <div className="flex-shrink-0 p-1.5 bg-[#FAF9F6]/60" data-testid="pos-action-grid">
            <div className="grid grid-cols-4 gap-1">
              {gridActions.map((a) => renderActionButton(a))}
            </div>
          </div>

          <div className="flex-shrink-0 px-1.5 pb-1.5 bg-[#FAF9F6]/60" data-testid="pos-summary-zone">
            <div className="rounded-xl border border-[#F0E0D4] bg-white overflow-hidden">
              <div className="px-2.5 pt-1.5 pb-1 flex items-center gap-1.5 min-h-[22px]">
                <span className="text-[9px] font-bold uppercase tracking-wider text-brand-brown/45 shrink-0">
                  {t("pos.colQty")}
                </span>
                <span className="text-[11px] font-bold text-brand-brown truncate">
                  {displayItemName(selectedItem) || t("pos.selected")}
                </span>
              </div>
              <div className="grid grid-cols-3 gap-1 p-1.5 pt-0">
                <button
                  type="button"
                  data-testid="qty-dec"
                  onClick={() => selectedLine && adjustQty(selectedLine, -1)}
                  disabled={!selectedLine || lineMutation.isPending}
                  className="pos-action-btn border border-[#F0E0D4] bg-[#FFFCFA] hover:bg-[#FFF3E8] active:scale-[0.97] grid place-items-center font-black text-brand-brown shadow-sm touch-manipulation disabled:opacity-40"
                >
                  <Minus size={13} strokeWidth={2.5} />
                </button>
                <button
                  type="button"
                  onClick={openKeypad}
                  disabled={!selectedLine}
                  className="pos-action-btn border-2 border-brand-orange bg-[#FFF3E8] hover:bg-[#FFE8D4] active:scale-[0.97] font-mono font-black text-[15px] text-brand-orange shadow-sm touch-manipulation tabular-nums disabled:opacity-40"
                  data-testid="pos-qty-keypad"
                  title="Tap to type quantity"
                >
                  {selectedItem?.qty || 0}
                </button>
                <button
                  type="button"
                  data-testid="qty-inc"
                  onClick={() => selectedLine && adjustQty(selectedLine, 1)}
                  disabled={!selectedLine || lineMutation.isPending}
                  className="pos-action-btn border border-brand-orange bg-brand-orange hover:bg-brand-orange-light active:scale-[0.97] grid place-items-center font-black text-white shadow-sm touch-manipulation disabled:opacity-40"
                >
                  <Plus size={13} strokeWidth={2.5} />
                </button>
              </div>
            </div>
          </div>

          <div className="flex-shrink-0 px-1.5 pb-1 bg-[#FAF9F6]/60">
            <div className="pos-grand-total overflow-hidden">
              <div className="px-2.5 py-1.5 flex items-center justify-between gap-2">
                <div className="text-left shrink-0 min-w-0">
                  <div className="text-[8px] uppercase tracking-[0.16em] font-bold text-brand-brown/55 leading-tight">{t("pos.grandTotal")}</div>
                  <div className="text-[10px] font-semibold text-brand-brown/45 tabular-nums mt-0.5">
                    {itemCount} / {qty2(qtyTotal)} · {currencyCode}
                  </div>
                </div>
                <div
                  className="font-mono font-extrabold tracking-tight leading-none tabular-nums text-brand-orange pos-grand-total-amount"
                  style={{ fontSize: 26, fontWeight: 800 }}
                  data-testid="grand-total"
                >
                  {money2(total)}
                </div>
              </div>
              <div className="pos-bill-breakdown" data-testid="pos-summary-metrics">
                <div>
                  <span>{t("pos.subTotal")}</span>
                  <span className="pos-bill-breakdown-value">{money2(subtotal)}</span>
                </div>
                {parcelCharge > 0 && (
                  <div>
                    <span>{t("pos.parcelChg")}</span>
                    <span className="pos-bill-breakdown-value">{money2(parcelCharge)}</span>
                  </div>
                )}
                {Math.abs(taxable - subtotal) > 0.009 && (
                  <div>
                    <span>{t("pos.taxable")}</span>
                    <span className="pos-bill-breakdown-value">{money2(taxable)}</span>
                  </div>
                )}
                <div>
                  <span>{t("pos.vat")}</span>
                  <span className="pos-bill-breakdown-value">{money2(vat)}</span>
                </div>
                {appliedDiscount > 0 && (
                  <div>
                    <span>{t("pos.discount", "Discount")}</span>
                    <span className="pos-bill-breakdown-value text-red-600">-{money2(appliedDiscount)}</span>
                  </div>
                )}
                {tipsOnPos && (
                  <div>
                    <button type="button" onClick={() => setOpMode("TIP")} className="hover:text-brand-orange text-left" data-testid="pos-tip-mode">
                      {t("pos.tip", "Tip")}
                    </button>
                    <span className="pos-bill-breakdown-value">{money2(tipAmount)}</span>
                  </div>
                )}
                {loyaltyOnPos && loyaltyDiscount > 0 && (
                  <div>
                    <span>Loyalty ({bill?.loyalty_points_redeemed} pts)</span>
                    <span className="pos-bill-breakdown-value text-red-600">-{money2(loyaltyDiscount)}</span>
                  </div>
                )}
                {Math.abs(roundOffAmt) >= 0.005 && (
                  <div>
                    <span>{t("pos.roundOff")}</span>
                    <span className="pos-bill-breakdown-value">{money2(roundOffAmt)}</span>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="flex-shrink-0 p-1.5 bg-[#FAF9F6]">
            <div className="pos-pay-card overflow-hidden">
              <div className="p-1.5 space-y-1.5 bg-white">
                <button
                  type="button"
                  data-testid="pay-now"
                  onClick={() => openPayDialog("cash")}
                  disabled={paymentMutation.isPending || !items.length || !billEditable}
                  className="w-full h-11 rounded-lg bg-brand-green hover:bg-[#1e9a52] text-white text-[13px] font-black tracking-wide flex items-center justify-center gap-1.5 shadow-md active:scale-[0.99] disabled:opacity-60 touch-manipulation min-h-[44px]"
                >
                  {paymentMutation.isPending ? (
                    <Loader2 size={15} className="animate-spin" />
                  ) : (
                    <>
                      <Wallet size={15} />
                      {t("pos.savePay")} · {aed(total)}
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </aside>
      </div>

      <PosKeypadPopup
        open={keypadOpen}
        onClose={() => setKeypadOpen(false)}
        modeConfig={currentMode}
        buffer={buffer}
        onDigit={pushDigit}
        onBackspace={backspace}
        onClear={clearBuffer}
        onApply={applyBuffer}
        onCancelMode={cancelMode}
      />

      {closeShiftOpen && (
        <div
          className="fixed inset-0 z-[200] flex items-center justify-center bg-brand-brown/25 backdrop-blur-[2px] p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="pos-close-shift-title"
          data-testid="pos-close-shift-dialog"
          onClick={() => !closeShiftMutation.isPending && setCloseShiftOpen(false)}
        >
          <div
            className="w-full max-w-sm bg-white rounded-xl border border-[#F0E0D4] shadow-2xl overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-4 py-3 border-b border-[#F5EBE3] bg-[#FFFCFA]">
              <h2 id="pos-close-shift-title" className="text-[15px] font-bold text-brand-brown flex items-center gap-2">
                <Lock size={15} className="text-red-600" />
                Close shift — {counterName}
              </h2>
              <p className="text-[11px] text-brand-brown/55 mt-0.5">
                End of day / counter close. This locks POS until the shift is opened again.
              </p>
            </div>
            <div className="p-4 space-y-3">
              <div className="rounded-lg bg-[#FAF9F6] border border-[#F0E0D4] p-3 text-[12px] space-y-1.5">
                <div className="flex justify-between gap-3">
                  <span className="text-brand-brown/55 font-semibold">Session sales</span>
                  <span className="font-mono font-bold text-brand-brown">
                    {aed(shiftData?.summary?.net_sales ?? 0)}
                  </span>
                </div>
                <div className="flex justify-between gap-3">
                  <span className="text-brand-brown/55 font-semibold">Expected cash</span>
                  <span className="font-mono font-bold text-brand-brown">
                    {aed(shiftData?.summary?.expected_cash ?? 0)}
                  </span>
                </div>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="pos-closing-cash">Physical cash counted (optional)</Label>
                <Input
                  id="pos-closing-cash"
                  type="number"
                  min="0"
                  step={moneyStep}
                  value={closingCash}
                  onChange={(e) => setClosingCash(e.target.value)}
                  data-testid="pos-closing-cash"
                  autoFocus
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="pos-close-notes">Notes (optional)</Label>
                <Input
                  id="pos-close-notes"
                  value={closeNotes}
                  onChange={(e) => setCloseNotes(e.target.value)}
                  placeholder="Variance reason, handover, etc."
                  data-testid="pos-close-notes"
                />
              </div>
            </div>
            <div className="px-4 py-3 border-t border-[#F5EBE3] flex justify-end gap-2 bg-[#FFFCFA]">
              <Button
                variant="outline"
                disabled={closeShiftMutation.isPending}
                onClick={() => setCloseShiftOpen(false)}
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                disabled={closeShiftMutation.isPending || !shiftData?.session?.id}
                data-testid="pos-close-shift-confirm"
                onClick={() =>
                  closeShiftMutation.mutate({
                    sessionId: shiftData.session.id,
                    counted: closingCash,
                    notes: closeNotes,
                  })
                }
              >
                {closeShiftMutation.isPending ? (
                  <Loader2 size={14} className="animate-spin mr-1.5" />
                ) : (
                  <Lock size={14} className="mr-1.5" />
                )}
                Close shift
              </Button>
            </div>
          </div>
        </div>
      )}

      <PosCashReceiptPopup
        open={payOpen}
        due={total}
        method={paymentMethod}
        roundOff={roundOff}
        onRoundOffChange={(checked) => {
          setRoundOff(checked);
          if (paymentMethod === "cash" && !tenderedDirty) {
            const base = Number(total) || 0;
            const next = checked ? base + calcRoundOff(base) : base;
            setTenderedAmount(money2(next));
          }
        }}
        onMethodChange={(next) => {
          setPaymentMethod(next);
          const base = Number(total) || 0;
          const payable = roundOff ? base + calcRoundOff(base) : base;
          if (next === "split") {
            setTenderedAmount("");
            setTenderedDirty(true);
          } else if (next === "cash") {
            setTenderedAmount(money2(payable));
            setTenderedDirty(false);
          }
        }}
        trn={cashReceiptTrn}
        onTrnChange={setCashReceiptTrn}
        customerName={cashReceiptName}
        onCustomerNameChange={setCashReceiptName}
        onPickCustomer={openCustomerDialog}
        tendered={tenderedAmount}
        keysPaused={customerDialogOpen}
        onDigit={appendTenderedDigit}
        onBackspace={backspaceTendered}
        onClear={clearTendered}
        onConfirm={() => {
          if (paymentMethod === "cash") {
            processPayment("cash", tenderedAmount || String(total));
          } else if (paymentMethod === "split") {
            processPayment("split", tenderedAmount);
          } else {
            processPayment("card");
          }
        }}
        onClose={() => !paymentMutation.isPending && setPayOpen(false)}
        pending={paymentMutation.isPending}
      />

      <PosSizePickerPopup
        open={Boolean(sizePickItem)}
        item={sizePickItem}
        pending={lineMutation.isPending}
        onPick={(size) => addItem(sizePickItem, size)}
        onClose={() => setSizePickItem(null)}
      />

      {deliveryDialogOpen && orderType === "DL" && bill && (
        <div
          className="fixed inset-0 z-[200] flex items-center justify-center bg-brand-brown/25 backdrop-blur-[2px] p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="pos-delivery-title"
          data-testid="pos-delivery-dialog"
          onClick={() => setDeliveryDialogOpen(false)}
        >
          <div
            className="w-full max-w-lg bg-white rounded-xl border border-[#F0E0D4] shadow-2xl overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <DeliveryPanel
              variant="dialog"
              bill={bill}
              branchId={branchId}
              onSave={saveDeliveryDetails}
              isSaving={updateBillMutation.isPending}
              onAssignDriver={(id) => assignDriverMutation.mutate(id)}
              onAutoAssign={() => assignDriverMutation.mutate(null)}
              isAssigningDriver={assignDriverMutation.isPending}
              onClose={() => setDeliveryDialogOpen(false)}
            />
          </div>
        </div>
      )}

      {returnDialogOpen && (
        <div
          className="fixed inset-0 z-[200] flex items-center justify-center bg-brand-brown/20 backdrop-blur-[2px] p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="pos-return-title"
          data-testid="pos-return-dialog"
          onClick={() => setReturnDialogOpen(false)}
        >
          <div
            className="w-full max-w-2xl max-h-[85vh] flex flex-col rounded-lg border bg-white shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="border-b px-4 py-3 flex items-center justify-between gap-3">
              <div>
                <h2 id="pos-return-title" className="text-lg font-semibold text-slate-900">
                  Sales Return
                </h2>
                <p className="text-xs text-slate-500 mt-0.5">Lookup a paid bill and refund items (F11)</p>
              </div>
              <button
                type="button"
                onClick={() => setReturnDialogOpen(false)}
                className="text-slate-500 hover:text-slate-800"
                aria-label="Close sales return"
              >
                <X size={18} />
              </button>
            </div>

            <div className="px-4 py-3 border-b flex gap-2">
              <Input
                value={returnReference}
                onChange={(e) => setReturnReference(e.target.value)}
                placeholder="Bill no. e.g. O-1042 or 1042"
                className="h-9"
                data-testid="return-reference"
                onKeyDown={(e) => {
                  if (e.key === "Enter" && returnReference.trim()) {
                    returnLookupMutation.mutate(returnReference.trim());
                  }
                }}
              />
              <Button
                className="h-9 bg-slate-800 hover:bg-slate-900"
                disabled={!returnReference.trim() || returnLookupMutation.isPending}
                onClick={() => returnLookupMutation.mutate(returnReference.trim())}
                data-testid="return-lookup"
              >
                {returnLookupMutation.isPending ? <Loader2 size={14} className="animate-spin" /> : "Lookup"}
              </Button>
            </div>

            <div className="flex-1 overflow-y-auto p-4 space-y-3">
              {!returnLookup && !returnLookupMutation.isPending && (
                <p className="text-sm text-slate-500 text-center py-10">
                  Enter the original bill number from a completed sale.
                </p>
              )}
              {returnLookup && (
                <>
                  <div className="rounded-md border bg-slate-50 px-3 py-2 text-[12px]">
                    <div className="font-bold text-slate-900">{returnLookup.bill.bill_number}</div>
                    <div className="text-slate-600">
                      {returnLookup.bill.customer_name} · Paid {formatPlain(returnLookup.bill.net_amount)} {currencyCode}
                      {returnLookup.bill.returned_amount > 0 && (
                        <span className="text-red-700"> · Refunded {formatPlain(returnLookup.bill.returned_amount)}</span>
                      )}
                    </div>
                  </div>

                  <div className="space-y-2">
                    {returnLookup.lines.map((line) => (
                      <div
                        key={line.sales_line_id}
                        className="grid grid-cols-[1fr_88px_88px] gap-2 items-center border rounded-md px-3 py-2"
                      >
                        <div>
                          <div className="text-[13px] font-bold text-slate-900">{displayItemName(line)}</div>
                          <div className="text-[10px] text-slate-500 font-mono">
                            {line.code} · sold {line.sold_qty} · left {line.returnable_qty}
                          </div>
                        </div>
                        <Input
                          type="number"
                          min={0}
                          max={line.returnable_qty}
                          step="1"
                          value={returnSelections[line.sales_line_id] ?? 0}
                          onChange={(e) => {
                            const v = Math.max(0, Math.min(line.returnable_qty, Number(e.target.value) || 0));
                            setReturnSelections((prev) => ({ ...prev, [line.sales_line_id]: v }));
                          }}
                          className="h-8 text-right font-mono"
                          data-testid={`return-qty-${line.sales_line_id}`}
                        />
                        <div className="text-right font-mono text-[12px] text-slate-700">
                          {money2(line.unit_amount * (returnSelections[line.sales_line_id] || 0))}
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="flex gap-2 pt-1">
                    {[
                      { id: "cash", label: "Cash" },
                      { id: "card", label: "Card" },
                      { id: "credit", label: "Credit" },
                    ].map((m) => (
                      <button
                        key={m.id}
                        type="button"
                        disabled={m.id === "credit" && !returnLookup.bill.customer_id}
                        onClick={() => setReturnRefundMethod(m.id)}
                        className={cn(
                          "flex-1 h-9 rounded-sm border-2 text-[12px] font-bold",
                          returnRefundMethod === m.id
                            ? "border-red-600 bg-red-50 text-red-900"
                            : "border-slate-300 bg-white text-slate-700",
                          m.id === "credit" && !returnLookup.bill.customer_id && "opacity-50 cursor-not-allowed",
                        )}
                      >
                        {m.label}
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>

            <div className="border-t px-4 py-3 flex items-center justify-between gap-3">
              <div className="text-[13px] font-bold text-slate-800">
                Refund: <span className="font-mono text-red-700">{formatWithCode(returnRefundPreview)}</span>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setReturnDialogOpen(false)}>Cancel</Button>
                <Button
                  className="bg-red-600 hover:bg-red-700 text-white"
                  disabled={!returnLookup || returnSubmitMutation.isPending || returnRefundPreview <= 0}
                  onClick={() => returnSubmitMutation.mutate()}
                  data-testid="return-submit"
                >
                  {returnSubmitMutation.isPending ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : null}
                  Process Return
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {comboMenuOpen && (
        <div
          className="fixed inset-0 z-[200] flex items-center justify-center bg-brand-brown/20 backdrop-blur-[2px] p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="pos-combo-title"
          data-testid="pos-combo-dialog"
          onClick={() => setComboMenuOpen(false)}
        >
          <div
            className="w-full max-w-xl max-h-[85vh] flex flex-col rounded-lg border bg-white shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="border-b px-4 py-3 flex items-center justify-between gap-3">
              <div>
                <h2 id="pos-combo-title" className="text-lg font-semibold text-slate-900">
                  Combo Items
                </h2>
                <p className="text-xs text-slate-500 mt-0.5">Adds combo header + kitchen components (Ctrl+C)</p>
              </div>
              <button
                type="button"
                onClick={() => setComboMenuOpen(false)}
                className="text-slate-500 hover:text-slate-800"
                aria-label="Close combo menu"
              >
                <X size={18} />
              </button>
            </div>
            <div className="p-4 space-y-2 overflow-y-auto">
              {menuCombos.length === 0 && (
                <p className="text-sm text-slate-500 text-center py-8">No combos configured for this branch.</p>
              )}
              {menuCombos.map((combo) => (
                <button
                  key={combo.menu_item_id}
                  type="button"
                  data-testid={`combo-${combo.code}`}
                  disabled={comboMutation.isPending}
                  onClick={() => addComboItem(combo)}
                  className="w-full rounded-md border-2 border-violet-300 bg-violet-50 hover:bg-violet-100 px-3 py-3 text-left active:translate-y-px disabled:opacity-60"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="text-[14px] font-black text-slate-900">{combo.name}</div>
                      <div className="text-[10px] font-mono text-slate-500 mt-0.5">{combo.code}</div>
                    </div>
                    <div className="text-[18px] font-black font-mono text-violet-900">{money2(combo.price)}</div>
                  </div>
                  <div className="mt-2 text-[11px] text-slate-600">
                    {(combo.components || []).map((c) => `${c.qty}× ${c.name}`).join(" · ")}
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {fastMenuOpen && (
        <div
          className="fixed inset-0 z-[200] flex items-center justify-center bg-brand-brown/20 backdrop-blur-[2px] p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="pos-fast-menu-title"
          data-testid="pos-fast-menu-dialog"
          onClick={() => setFastMenuOpen(false)}
        >
          <div
            className="w-full max-w-2xl max-h-[85vh] flex flex-col rounded-lg border bg-white shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="border-b px-4 py-3 flex items-center justify-between gap-3">
              <div>
                <h2 id="pos-fast-menu-title" className="text-lg font-semibold text-slate-900">
                  Fast Menu
                </h2>
                <p className="text-xs text-slate-500 mt-0.5">
                  {fastMenuEnabled
                    ? "Press F1–F12 to add items (F8 = KOT note, F10 = reprint)"
                    : "Enable shortcuts in Restaurant Setup → POS Preferences"}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setFastMenuOpen(false)}
                className="text-slate-500 hover:text-slate-800"
                aria-label="Close fast menu"
              >
                <X size={18} />
              </button>
            </div>
            <div className="p-4 grid grid-cols-3 sm:grid-cols-4 gap-2 overflow-y-auto">
              {(fastMenuData?.slots || []).map((slot) => {
                const assigned = Boolean(slot.menu_item_id);
                const reserved = slot.slot_key === "F8" || slot.slot_key === "F10";
                return (
                  <button
                    key={slot.slot_key}
                    type="button"
                    data-testid={`fast-slot-${slot.slot_key}`}
                    disabled={!assigned || !fastMenuEnabled || reserved}
                    onClick={() => addFastSlotItem(slot)}
                    className={cn(
                      "min-h-[72px] rounded-md border-2 px-2 py-2 text-left flex flex-col justify-between",
                      assigned && fastMenuEnabled && !reserved
                        ? "border-amber-500 bg-amber-50 hover:bg-amber-100 active:translate-y-px"
                        : "border-slate-200 bg-slate-50 opacity-70 cursor-not-allowed",
                    )}
                  >
                    <span className="text-[10px] font-black text-slate-500">{slot.slot_key}</span>
                    <span className="text-[12px] font-bold text-slate-900 leading-tight line-clamp-2">
                      {reserved ? (slot.slot_key === "F8" ? "KOT NOTE" : "REPRINT") : (slot.name || "—")}
                    </span>
                    {assigned && !reserved && (
                      <span className="text-[10px] font-mono text-slate-600">{money2(slot.price)}</span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {kotReprintOpen && (
        <div
          className="fixed inset-0 z-[200] flex items-center justify-center bg-brand-brown/20 backdrop-blur-[2px] p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="pos-kot-reprint-title"
          data-testid="pos-kot-reprint-dialog"
          onClick={() => setKotReprintOpen(false)}
        >
          <div
            className="w-full max-w-lg max-h-[85vh] flex flex-col rounded-lg border bg-white shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="border-b px-4 py-3 flex items-center justify-between gap-3">
              <div>
                <h2 id="pos-kot-reprint-title" className="text-lg font-semibold text-slate-900">
                  KOT Reprint
                </h2>
                <p className="text-xs text-slate-500 mt-0.5">
                  Re-fire kitchen tickets for {bill?.bill_number} (F6 · legacy KOTPrint)
                </p>
              </div>
              <button
                type="button"
                onClick={() => setKotReprintOpen(false)}
                className="text-slate-500 hover:text-slate-800"
                aria-label="Close KOT reprint"
              >
                <X size={18} />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-4 space-y-2">
              {kotTicketsLoading && (
                <div className="py-10 grid place-items-center text-slate-500 gap-2">
                  <Loader2 className="h-5 w-5 animate-spin" />
                  <span className="text-sm">Loading KOT tickets…</span>
                </div>
              )}
              {!kotTicketsLoading && !(kotTicketsData?.tickets?.length) && (
                <p className="text-sm text-slate-500 text-center py-10">
                  No kitchen tickets on this bill yet — add KOT items first.
                </p>
              )}
              {(kotTicketsData?.tickets || []).map((ticket) => (
                <div
                  key={ticket.id}
                  className="border rounded-md px-3 py-2.5 flex items-start justify-between gap-3"
                  data-testid={`kot-reprint-row-${ticket.id}`}
                >
                  <div className="min-w-0">
                    <div className="font-bold text-slate-900 text-[13px]">{ticket.kot_number}</div>
                    <div className="text-[11px] text-slate-500">
                      {ticket.station} · {ticket.status}
                      {ticket.printer_name ? ` · ${ticket.printer_name}` : ""}
                      {ticket.print_count > 0 ? ` · printed ×${ticket.print_count}` : ""}
                    </div>
                    <div className="text-[11px] text-slate-600 mt-1 truncate">
                      {(ticket.items || []).map((it) => `${it.name} ×${it.qty}`).join(", ")}
                    </div>
                  </div>
                  <Button
                    size="sm"
                    className="h-8 shrink-0 bg-amber-500 hover:bg-amber-600"
                    disabled={kotReprintMutation.isPending}
                    onClick={() => kotReprintMutation.mutate([ticket.id])}
                    data-testid={`kot-reprint-btn-${ticket.id}`}
                  >
                    <Flame size={13} className="mr-1" /> Reprint
                  </Button>
                </div>
              ))}
            </div>

            <div className="border-t px-4 py-3 flex justify-between gap-2">
              <Button variant="outline" onClick={() => setKotReprintOpen(false)}>Close</Button>
              <Button
                className="bg-slate-900 hover:bg-slate-800"
                disabled={kotReprintMutation.isPending || !(kotTicketsData?.tickets?.length)}
                onClick={() => kotReprintMutation.mutate(null)}
                data-testid="kot-reprint-all"
              >
                {kotReprintMutation.isPending ? (
                  <Loader2 size={14} className="animate-spin" />
                ) : (
                  <>
                    <PrinterIcon size={14} className="mr-1.5" /> Reprint all stations
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      )}

      <PosBillReprintPopup
        open={billReprintOpen}
        dateFrom={reprintDateFrom}
        dateTo={reprintDateTo}
        search={reprintSearch}
        onDateFromChange={setReprintDateFrom}
        onDateToChange={setReprintDateTo}
        onSearchChange={setReprintSearch}
        bills={reprintBills}
        loading={reprintLoading}
        pending={reprintBillMutation.isPending}
        lastPaidBillId={lastPaidBillRef.current?.id ?? null}
        onReprint={(row) => {
          if (!row?.id) return;
          prepareBillPrintWindow();
          reprintBillMutation.mutate(row.id);
        }}
        onClose={() => {
          if (reprintBillMutation.isPending) return;
          setBillReprintOpen(false);
        }}
      />

      <PosCreditPopup
        open={creditDialogOpen}
        due={total}
        billCustomerId={bill?.customer_id}
        customers={customerOptions}
        loading={customersLoading}
        search={customerSearch}
        onSearchChange={setCustomerSearch}
        pending={paymentMutation.isPending || updateBillMutation.isPending}
        selected={creditCustomer}
        onSelectCustomer={(c) => setCreditCustomer(c)}
        onChangeCustomer={() => setCreditCustomer(null)}
        onCreateCustomer={async (details) => {
          try {
            return await createCreditCustomer(details);
          } catch (err) {
            toast.error(
              err.response?.data?.errors?.name?.[0]
                || err.response?.data?.errors?.code?.[0]
                || err.response?.data?.message
                || "Failed to create customer",
            );
            return null;
          }
        }}
        onCharge={chargeCreditSale}
        onClose={() => {
          if (paymentMutation.isPending) return;
          setCreditDialogOpen(false);
          setCreditCustomer(null);
        }}
      />

      {customerDialogOpen && (
        <div
          className="fixed inset-0 z-[220] flex items-center justify-center bg-brand-brown/20 backdrop-blur-[2px] p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="pos-customer-title"
          data-testid="pos-customer-dialog"
          onClick={() => setCustomerDialogOpen(false)}
        >
          <div
            className="w-full max-w-md max-h-[85vh] flex flex-col rounded-lg border bg-white shadow-2xl"
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.stopPropagation()}
          >
            <div className="border-b px-4 py-3">
              <h2 id="pos-customer-title" className="text-lg font-semibold text-slate-900">
                {t("pos.selectCustomer", "Select customer")}
              </h2>
              <p className="text-xs text-slate-500 mt-0.5">
                {t("pos.selectCustomerHint", "Choose a registered customer or Walk-in")}
              </p>
              <div className="mt-3 relative">
                <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input
                  value={customerSearch}
                  onChange={(e) => setCustomerSearch(e.target.value)}
                  placeholder={t("pos.searchCustomer", "Search name, code, phone…")}
                  className="h-9 pl-8"
                  autoFocus
                  data-testid="pos-customer-search"
                />
              </div>
            </div>
            <div className="max-h-72 overflow-y-auto border-b">
              <button
                type="button"
                className={cn(
                  "w-full text-left px-4 py-3 text-sm hover:bg-slate-50 border-b flex items-center gap-2",
                  !bill?.customer_id && "bg-blue-50",
                )}
                onClick={() => applyCustomer(null)}
                data-testid="pos-customer-walkin"
              >
                <User size={14} className="text-slate-400 shrink-0" />
                <span className="font-semibold">Walk-in</span>
                {!bill?.customer_id && (
                  <span className="text-[10px] text-blue-700 font-bold ms-auto">CURRENT</span>
                )}
              </button>
              {customersLoading && customerOptions.length === 0 && (
                <p className="px-4 py-6 text-sm text-slate-500 text-center flex items-center justify-center gap-2">
                  <Loader2 size={14} className="animate-spin" /> Loading…
                </p>
              )}
              {customerOptions.map((c) => (
                <button
                  key={c.id}
                  type="button"
                  className={cn(
                    "w-full text-left px-4 py-3 text-sm hover:bg-slate-50 border-b last:border-b-0",
                    bill?.customer_id === c.id && "bg-blue-50",
                  )}
                  onClick={() => applyCustomer(c)}
                  data-testid={`pos-customer-${c.id}`}
                >
                  <div className="font-semibold text-slate-900">{c.name}</div>
                  <div className="text-[11px] text-slate-500 mt-0.5">
                    {[c.code, c.phone, c.mobile].filter(Boolean).join(" · ") || "—"}
                  </div>
                </button>
              ))}
              {!customersLoading && customerOptions.length === 0 && customerSearch && (
                <p className="px-4 py-6 text-sm text-slate-500 text-center">No customers found</p>
              )}
            </div>
            <div className="flex justify-end gap-2 px-4 py-3">
              <Button variant="outline" onClick={() => setCustomerDialogOpen(false)}>
                {t("common.cancel", "Cancel")}
              </Button>
            </div>
          </div>
        </div>
      )}

      {loyaltyDialogOpen && (
        <div
          className="fixed inset-0 z-[200] flex items-center justify-center bg-brand-brown/20 backdrop-blur-[2px] p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="pos-loyalty-title"
          data-testid="pos-loyalty-dialog"
          onClick={() => setLoyaltyDialogOpen(false)}
        >
          <div
            className="w-full max-w-sm rounded-lg border bg-white shadow-2xl p-4 space-y-3"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 id="pos-loyalty-title" className="text-lg font-semibold">Redeem loyalty points</h2>
            <p className="text-sm text-slate-600">
              Balance: <strong>{loyaltySummary?.balance ?? 0}</strong> pts
              {loyaltySummary?.settings?.min_redeem_points
                ? ` · Min ${loyaltySummary.settings.min_redeem_points}`
                : ""}
            </p>
            <div>
              <Label>Points to redeem</Label>
              <Input
                type="number"
                value={redeemPoints}
                onChange={(e) => setRedeemPoints(e.target.value)}
                data-testid="pos-redeem-points-input"
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setLoyaltyDialogOpen(false)}>Cancel</Button>
              <Button
                onClick={() => redeemLoyaltyMutation.mutate(Number(redeemPoints))}
                disabled={redeemLoyaltyMutation.isPending || !redeemPoints}
              >
                Apply
              </Button>
            </div>
          </div>
        </div>
      )}

      {knoteDialogOpen && (
        <div
          className="fixed inset-0 z-[200] flex items-center justify-center bg-brand-brown/20 backdrop-blur-[2px] p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="pos-knote-title"
          data-testid="pos-knote-dialog"
          onClick={() => setKnoteDialogOpen(false)}
        >
          <div
            className="w-full max-w-md max-h-[85vh] flex flex-col rounded-lg border bg-white shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="border-b px-4 py-3">
              <h2 id="pos-knote-title" className="text-lg font-semibold text-slate-900">
                Kitchen Notes — {items.find((i) => i.id === selectedLine)?.name || "Line"}
              </h2>
              <p className="text-xs text-slate-500 mt-0.5">Select notes to print on KOT (F8)</p>
            </div>
            <div className="max-h-72 overflow-y-auto divide-y border-b">
              {kitchenNotesLoading && kitchenNoteOptions.length === 0 && (
                <p className="px-3 py-6 text-sm text-slate-500 text-center flex items-center justify-center gap-2">
                  <Loader2 size={14} className="animate-spin" /> Loading notes…
                </p>
              )}
              {kitchenNoteOptions.map((note) => (
                <label
                  key={note.id}
                  className="flex items-center gap-2 px-3 py-2.5 text-sm cursor-pointer hover:bg-slate-50"
                >
                  <Checkbox
                    checked={knoteSelection.includes(note.id)}
                    onCheckedChange={() => toggleKnoteOption(note.id)}
                  />
                  <span className="text-[10px] font-mono text-slate-500 w-12">{note.code}</span>
                  <span className="flex-1">{note.name}</span>
                  <span className="text-[10px] text-slate-400">{note.group}</span>
                </label>
              ))}
              {kitchenNoteOptions.length === 0 && (
                <p className="px-3 py-6 text-sm text-slate-500 text-center">
                  No kitchen notes yet — type one below to add it
                </p>
              )}
            </div>
            <div className="flex gap-2 px-4 py-2 border-b bg-[#FFFCFA]">
              <Input
                value={knoteNewName}
                onChange={(e) => setKnoteNewName(e.target.value)}
                placeholder="Add note e.g. No salt"
                className="h-9"
                data-testid="pos-knote-new"
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    const name = knoteNewName.trim();
                    if (name) createKitchenNoteMutation.mutate(name);
                  }
                }}
              />
              <Button
                type="button"
                variant="outline"
                className="h-9 shrink-0"
                disabled={!knoteNewName.trim() || createKitchenNoteMutation.isPending}
                onClick={() => createKitchenNoteMutation.mutate(knoteNewName.trim())}
                data-testid="pos-knote-add"
              >
                {createKitchenNoteMutation.isPending ? <Loader2 size={14} className="animate-spin" /> : "Add"}
              </Button>
            </div>
            <div className="flex justify-end gap-2 px-4 py-3">
              <Button variant="outline" onClick={() => setKnoteSelection([])}>Clear</Button>
              <Button variant="outline" onClick={() => setKnoteDialogOpen(false)}>Cancel</Button>
              <Button
                className="bg-[#16A34A] hover:bg-emerald-700 text-white"
                onClick={saveKitchenNotes}
                disabled={kitchenNoteMutation.isPending || !selectedLine}
                data-testid="pos-knote-save"
              >
                {kitchenNoteMutation.isPending ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : <Save size={14} className="mr-1.5" />}
                Save Notes
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function ViewTab({ active, onClick, icon, label, count, testid, compact = false }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={cn(
        "flex items-center gap-1 px-3 text-[10px] font-bold uppercase tracking-wider border-r border-[#F5EBE3] touch-manipulation",
        compact ? "h-9 min-h-[36px]" : "h-11 min-h-[48px]",
        active
          ? "bg-white text-brand-orange border-b-2 border-b-brand-orange -mb-px"
          : "bg-[#FFFCFA] text-brand-brown/50 hover:text-brand-brown",
      )}
    >
      {icon}
      <span>{label}</span>
      {count !== null && (
        <span className={cn(
          "text-[9px] font-mono px-1.5 py-0.5 rounded-full",
          active ? "bg-brand-orange/15 text-brand-orange" : "bg-white text-brand-brown/40",
        )}>
          {count}
        </span>
      )}
    </button>
  );
}

